import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  lazy,
  Suspense,
  type ReactNode,
  type ComponentType,
  type ErrorInfo,
} from "react";
import { Spin } from "antd";
import { useSelector } from "react-redux";
import { UserType } from "@tcr-erp/shared";
import { useAuthBootstrap } from "@/hooks/useAuthBootstrap";
import { useAuthContextSocket } from "@/hooks/useAuthContextSocket";
import type { AuthUser } from "@/redux/slices/authSlice";
import { reportClientError } from "@/services/clientError.service";
import {
  ATTRIBUTE_GATE,
  BRAND_GATE,
  CATEGORY_GATE,
  UNIT_GATE,
} from "@/constants/productCatalogueGates";

// Eager pages (entry + list of main modules)
import { LoginPage } from "@/pages/Login";
import { POSPage } from "@/pages/POS";
import { ProductsPage } from "@/pages/Products";
import { OrdersPage } from "@/pages/Orders";
import { NotFoundPage } from "@/pages/NotFound";

import { Layout } from "@/components/Layout";
import { PrivateRoute } from "@/components/PrivateRoute";
import { RoleGuard } from "@/components/PrivateRoute/RoleGuard";
import { StoreGuard } from "@/components/PrivateRoute/StoreGuard";
import { FeatureGate } from "@/components/FeatureGate";
import { RouteErrorBoundary } from "@/components/shared/RouteErrorBoundary";
import {
  HR_IMPORT_WRITE_PERMISSIONS,
  HR_IMPORT_ANY_WRITE_PERMISSION,
  ROUTES,
} from "@/constants/routes";
// TCR-2542 — gate route /report-center bằng hợp OR key trong report registry.
import { REPORT_CENTER_ANY_PERMISSIONS } from "@/pages/ReportCenter/reportRegistry";
import { isLazyChunkError, loadLazyModule } from "@/utils/lazyChunk";
import {
  MINIAPP_WAREHOUSE_ACCESS_PERMISSION,
  MINIAPP_STOCK_LOOKUP_PERMISSION,
} from "@/pages/Miniapp/permissions";

// Re-export — chỗ khác trong repo (vd tests) đang import 2 hàm này từ đây.
export { isLazyChunkError, loadLazyModule };

/**
 * Load a route chunk with a short retry window. A transient mobile/LAN
 * connection or Vite HMR hiccup should not force a full document reload;
 * after retries, the route error boundary presents a user-controlled retry.
 */
function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(() => loadLazyModule(factory));
}

// Lazy pages (less-frequented or heavy)
const InventoryPage = lazyWithReload(() =>
  import("@/pages/Inventory").then((m) => ({ default: m.InventoryPage })),
);
const MiniappLayout = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappLayout })),
);
const MiniappHomePage = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappHomePage })),
);
const MiniappScanPage = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappScanPage })),
);
const MiniappCountsPage = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappCountsPage })),
);
const MiniappReceivingPage = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappReceivingPage })),
);
const InventoryCostingPage = lazyWithReload(() =>
  import("@/pages/Inventory/costing").then((m) => ({
    default: m.InventoryCostingPage,
  })),
);
const InventoryCostExceptionsPage = lazyWithReload(() =>
  import("@/pages/Inventory/cost-exceptions").then((m) => ({
    default: m.InventoryCostExceptionsPage,
  })),
);
const WarehousesPage = lazyWithReload(() =>
  import("@/pages/Inventory/warehouses").then((m) => ({
    default: m.WarehousesPage,
  })),
);
const WarehouseNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/warehouses/new").then((m) => ({
    default: m.WarehouseNewPage,
  })),
);
const WarehouseLocationsPage = lazyWithReload(() =>
  import("@/pages/Inventory/warehouse-locations").then((m) => ({
    default: m.WarehouseLocationsPage,
  })),
);
const MiniappAllocationVoucherPage = lazyWithReload(() =>
  import("@/pages/Miniapp/allocation-voucher").then((m) => ({
    default: m.MiniappAllocationVoucherPage,
  })),
);
const InventoryLotsPage = lazyWithReload(() =>
  import("@/pages/Inventory/lots").then((m) => ({
    default: m.InventoryLotsPage,
  })),
);
const StockRecallsPage = lazyWithReload(() =>
  import("@/pages/Inventory/recalls").then((m) => ({
    default: m.StockRecallsPage,
  })),
);
const StockRecallNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/recalls/new").then((m) => ({
    default: m.StockRecallNewPage,
  })),
);
const StockRecallDetailPage = lazyWithReload(() =>
  import("@/pages/Inventory/recalls/detail").then((m) => ({
    default: m.StockRecallDetailPage,
  })),
);
const StaffsPage = lazyWithReload(() =>
  import("@/pages/Staffs").then((m) => ({ default: m.StaffsPage })),
);
const ReportsPage = lazyWithReload(() =>
  import("@/pages/Reports").then((m) => ({ default: m.ReportsPage })),
);
// TCR-2542 — Trung tâm báo cáo (danh mục + outlet chi tiết theo :reportId).
const ReportCenterPage = lazyWithReload(() =>
  import("@/pages/ReportCenter").then((m) => ({ default: m.ReportCenterPage })),
);
const ReportOutletPage = lazyWithReload(() =>
  import("@/pages/ReportCenter/ReportOutlet").then((m) => ({
    default: m.ReportOutletPage,
  })),
);
const HardwarePage = lazyWithReload(() =>
  import("@/pages/Hardware").then((m) => ({ default: m.HardwarePage })),
);
const StoreSettingsPage = lazyWithReload(() =>
  import("@/pages/StoreSettings").then((m) => ({
    default: m.StoreSettingsPage,
  })),
);
const ThemeSettingsPage = lazyWithReload(() =>
  import("@/pages/ThemeSettings").then((m) => ({
    default: m.ThemeSettingsPage,
  })),
);
const HrmDashboardPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmDashboardPage })),
);
const HrmBaseVNPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmBaseVNPage })),
);
const HrmBaseVNSyncLogPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmBaseVNSyncLogPage })),
);
const HrmSettingsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmSettingsPage })),
);
const HrmAttendancePolicyPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmAttendancePolicyPage })),
);
const HrmAttendanceConfigPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmAttendanceConfigPage })),
);
const HrmEmployeesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmEmployeesPage })),
);
const HrmEmployeeDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmEmployeeDetailPage })),
);
const HrmEmployeeFormPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmEmployeeFormPage })),
);
const HrmEmployeeSelfServicePage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmEmployeeSelfServicePage,
  })),
);
const HrmEmployeeChangeRequestsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmEmployeeChangeRequestsPage,
  })),
);
const HrmOfficesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmOfficesPage })),
);
const HrmPositionsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmPositionsPage })),
);
const HrmPositionDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmPositionDetailPage })),
);
const HrmLegalEntitiesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmLegalEntitiesPage })),
);
const HrmLegalEntityDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmLegalEntityDetailPage })),
);
const HrmAssetTypesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmAssetTypesPage })),
);
const HrmInternalDocumentsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmInternalDocumentsPage })),
);
const HrmDocumentTypesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmDocumentTypesPage })),
);
const HrmWorkRegionsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWorkRegionsPage })),
);
const HrmInternalDocumentFormPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmInternalDocumentFormPage,
  })),
);
const HrmInternalDocumentDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmInternalDocumentDetailPage,
  })),
);
const HrmInternalDocumentImportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmInternalDocumentImportPage,
  })),
);
const HrmInternalDecisionImportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmInternalDecisionImportPage,
  })),
);
const HrmContractsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmContractsPage })),
);
const HrmContractFormPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmContractFormPage })),
);
const HrmContractDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmContractDetailPage })),
);
const HrmKpiScorecardImportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmKpiScorecardImportPage })),
);
const HrmKpiScorecardsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmKpiScorecardsPage })),
);
const HrmPayrollPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmPayrollPage })),
);
const HrmBenefitsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmBenefitsPage })),
);
const ShipperLayout = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({ default: m.ShipperLayout })),
);
const ShipperHomeRedirect = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperHomeRedirect,
  })),
);
const ShipperOrdersPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({ default: m.ShipperOrdersPage })),
);
const ShipperClaimOrdersPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperClaimOrdersPage,
  })),
);
const ShipperOrderDetailPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperOrderDetailPage,
  })),
);
const ShipperCodPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({ default: m.ShipperCodPage })),
);
const ShipperNotificationsPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperNotificationsPage,
  })),
);
const ShipperProfilePage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({ default: m.ShipperProfilePage })),
);
const ShipperDeliveryStatsPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperDeliveryStatsPage,
  })),
);
const ShipperCodStatsPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperCodStatsPage,
  })),
);
const ShipperCodReconciliationPage = lazyWithReload(() =>
  import("@/pages/ShipperApp").then((m) => ({
    default: m.ShipperCodReconciliationPage,
  })),
);
const HrmInsuranceRosterPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmInsuranceRosterPage })),
);
const HrmAttendancePage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmAttendancePage })),
);
const HrmWorkflowsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWorkflowsPage })),
);
const HrmWorkflowBoardPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWorkflowBoardPage })),
);
const HrmWorkflowJobDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWorkflowJobDetailPage })),
);
const HrmWorkflowsReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWorkflowsReportPage })),
);
const HrmWeworksPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWeworksPage })),
);
const HrmWeworkProjectDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmWeworkProjectDetailPage,
  })),
);
const HrmWeworksReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmWeworksReportPage })),
);
const HrmRequestsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmRequestsPage })),
);
const HrmRequestDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmRequestDetailPage })),
);
const HrmRequestsReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmRequestsReportPage })),
);
const HrmSchedulesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmSchedulesPage })),
);
const HrmOvertimePage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmOvertimePage })),
);
const HrmSchedulesReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmSchedulesReportPage })),
);
const HrmTimeoffsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmTimeoffsPage })),
);
const HrmTimeoffsReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmTimeoffsReportPage })),
);
const HrmTurnoverReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmTurnoverReportPage })),
);
const HrmTrendReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmTrendReportPage })),
);
const HrmPolicyReportDocumentsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmPolicyReportDocumentsPage })),
);
const HrmAnnualLeaveReportPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmAnnualLeaveReportPage })),
);
const HrmRecruitmentPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmRecruitmentPage })),
);
const HrmRecruitmentRequestFormPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmRecruitmentRequestFormPage,
  })),
);
const HrmRecruitmentDetailPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({ default: m.HrmRecruitmentDetailPage })),
);
const HrmRecruitmentCandidateFormPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmRecruitmentCandidateFormPage,
  })),
);
const HrmRecruitmentCandidatesPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmRecruitmentCandidatesPage,
  })),
);
const HrmRecruitmentApprovalsPage = lazyWithReload(() =>
  import("@/pages/HRM").then((m) => ({
    default: m.HrmRecruitmentApprovalsPage,
  })),
);

// Lazy detail / form pages
const ProductDetailPage = lazyWithReload(() =>
  import("@/pages/Products/detail").then((m) => ({
    default: m.ProductDetailPage,
  })),
);
const ProductNewPage = lazyWithReload(() =>
  import("@/pages/Products/new").then((m) => ({ default: m.ProductNewPage })),
);
const ProductEditPage = lazyWithReload(() =>
  import("@/pages/Products/edit").then((m) => ({ default: m.ProductEditPage })),
);

// Services — EG catalog (mirrors Products)
const ServicesPage = lazyWithReload(() =>
  import("@/pages/Services").then((m) => ({ default: m.ServicesPage })),
);
const ServiceDetailPage = lazyWithReload(() =>
  import("@/pages/Services/detail").then((m) => ({
    default: m.ServiceDetailPage,
  })),
);
const ServiceNewPage = lazyWithReload(() =>
  import("@/pages/Services/new").then((m) => ({ default: m.ServiceNewPage })),
);
const ServiceEditPage = lazyWithReload(() =>
  import("@/pages/Services/edit").then((m) => ({ default: m.ServiceEditPage })),
);
const OrderDetailPage = lazyWithReload(() =>
  import("@/pages/Orders/detail").then((m) => ({ default: m.OrderDetailPage })),
);
const OrderEditPage = lazyWithReload(() =>
  import("@/pages/Orders/edit").then((m) => ({ default: m.OrderEditPage })),
);
const OrderImportsPage = lazyWithReload(() =>
  import("@/pages/Orders/OrderImportsPage").then((m) => ({
    default: m.OrderImportsPage,
  })),
);
const StaffDetailPage = lazyWithReload(() =>
  import("@/pages/Staffs/detail").then((m) => ({ default: m.StaffDetailPage })),
);

const SalesReportPage = lazyWithReload(() =>
  import("@/pages/Reports/sales").then((m) => ({ default: m.SalesReportPage })),
);
const InventoryReportPage = lazyWithReload(() =>
  import("@/pages/Reports/inventory").then((m) => ({
    default: m.InventoryReportPage,
  })),
);
const EodReportPage = lazyWithReload(() =>
  import("@/pages/Reports/eod").then((m) => ({ default: m.EodReportPage })),
);
const EcomOrdersPage = lazyWithReload(
  () => import("@/pages/ECom/orders/index"),
);
const EcomReportsOverviewPage = lazyWithReload(
  () => import("@/pages/ECom/reports/overview"),
);
const EcomPerformancePage = lazyWithReload(
  () => import("@/pages/ECom/performance/index"),
);
const EcomProductsReportPage = lazyWithReload(
  () => import("@/pages/ECom/reports/products"),
);
const EcomSalesStatsPage = lazyWithReload(
  () => import("@/pages/ECom/reports/sales-stats"),
);
const EcomStaffStatsPage = lazyWithReload(
  () => import("@/pages/ECom/reports/staff-stats"),
);
const EcomRevenueReportPage = lazyWithReload(
  () => import("@/pages/ECom/reports/revenue"),
);
const EcomExpenseReportPage = lazyWithReload(
  () => import("@/pages/ECom/reports/expense"),
);
const EcomReturnCancelTrackingPage = lazyWithReload(
  () => import("@/pages/ECom/reports/return-cancel-tracking"),
);
// TCR-2094/2095 — màn quản lý BusinessBrand (CRUD + gán/gỡ shop), nền tảng filter Brand cho
// Báo cáo ngày TikTok Shop & Shopee (Task 7/8).
const EcomBusinessBrandsPage = lazyWithReload(
  () => import("@/pages/ECom/settings/business-brands"),
);
// TCR-2094 — Báo cáo ngày TikTok Shop (Task 7): GMV/NMV/số đơn tự động từ oc_orders +
// Views/Visits/Ads nhập tay (modal). Shopee dùng lại chung bộ API ở Task 8.
const EcomTiktokDailyReportPage = lazyWithReload(
  () => import("@/pages/ECom/reports/tiktok-daily"),
);
// TCR-2095 — Báo cáo ngày Shopee (Task 8): dùng lại chung 2 API của Task 7, chỉ khác
// channel='SHOPEE' và nhãn cột PDPView/Traffic.
const EcomShopeeDailyReportPage = lazyWithReload(
  () => import("@/pages/ECom/reports/shopee-daily"),
);
const CancelOrdersPage = lazyWithReload(
  () => import("@/pages/ECom/cancel-orders/CancelOrdersPage"),
);
const EcomReturnsPage = lazyWithReload(() => import("@/pages/ECom/returns"));
const EcomReconciliationPage = lazyWithReload(
  () => import("@/pages/ECom/reconciliation"),
);
const EcomOrderDetailPage = lazyWithReload(
  () => import("@/pages/ECom/orders/detail"),
);
const EcomOrderExceptionsPage = lazyWithReload(
  () => import("@/pages/ECom/orders/exceptions"),
);
const EcomOrderNewPage = lazyWithReload(() =>
  import("@/pages/ECom").then((m) => ({ default: m.EcomOrderNewPage })),
);
const EcomSettingsPage = lazyWithReload(() =>
  import("@/pages/ECom").then((m) => ({ default: m.EcomSettingsPage })),
);
const EcomOAuthResultPage = lazyWithReload(() =>
  import("@/pages/ECom").then((m) => ({ default: m.EcomOAuthResultPage })),
);
const EcomDerivedPage = lazyWithReload(() =>
  import("@/pages/ECom").then((m) => ({ default: m.EcomDerivedPage })),
);
const EcomFulfillmentPage = lazyWithReload(() =>
  import("@/pages/ECom").then((m) => ({ default: m.EcomFulfillmentPage })),
);
const EcomPackingPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/packing"),
);
const EcomHandoverPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/handover"),
);
const EcomWavesPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/waves"),
);
const EcomBatchPackingWavePage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/BatchPackingWavePage"),
);
const EcomInitLogisticPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/init-logistic"),
);
const EcomOrderPackingPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/order-packing"),
);
const EcomOrderSendingPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/order-sending"),
);
const MiniappOutboundPage = lazyWithReload(() =>
  import("@/pages/Miniapp").then((m) => ({ default: m.MiniappOutboundPage })),
);
const MiniappWavePickingPage = lazyWithReload(() =>
  import("@/pages/Miniapp/wave-picking").then((m) => ({ default: m.MiniappWavePickingPage })),
);
const EcomFulfillmentSettingsPage = lazyWithReload(
  () => import("@/pages/ECom/fulfillment/settings"),
);
const OmnichatInboxPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({ default: m.OmnichatInboxPage })),
);
const OmnichatConnectionsPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatConnectionsPage,
  })),
);
const OmnichatCareWorklistPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatCareWorklistPage,
  })),
);
const OmnichatCareRulesPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatCareRulesPage,
  })),
);
const OmnichatCampaignListPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatCampaignListPage,
  })),
);
const OmnichatCampaignEditorPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatCampaignEditorPage,
  })),
);
const OmnichatSettingsPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatSettingsPage,
  })),
);
const OmnichatSlaReportPage = lazyWithReload(() =>
  import("@/pages/Omnichat").then((m) => ({
    default: m.OmnichatSlaReportPage,
  })),
);
const B2BPage = lazyWithReload(() => import("@/pages/B2B"));
const B2BPricingPage = lazyWithReload(() => import("@/pages/B2B/Pricing"));
const B2BPricingNewPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/new"),
);
const B2BPricingDetailPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/detail"),
);
const B2BPricingEditPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/edit"),
);
const B2BPriceOverrideNewPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/overrides/new"),
);
const B2BPriceOverrideDetailPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/overrides/detail"),
);
const B2BPriceOverrideEditPage = lazyWithReload(
  () => import("@/pages/B2B/Pricing/overrides/edit"),
);
const B2BRebatesPage = lazyWithReload(() => import("@/pages/B2B/Rebates"));
const B2BRebatesNewPage = lazyWithReload(
  () => import("@/pages/B2B/Rebates/new"),
);
const B2BRebatesDetailPage = lazyWithReload(
  () => import("@/pages/B2B/Rebates/detail"),
);
const B2BRebatesEditPage = lazyWithReload(
  () => import("@/pages/B2B/Rebates/edit"),
);
const B2BRebateCustomerLedgerDetailPage = lazyWithReload(
  () => import("@/pages/B2B/Rebates/RebateCustomerLedgerDetailPage"),
);
const B2BReceivablesPage = lazyWithReload(
  () => import("@/pages/B2B/Receivables"),
);
const B2BReceivableCustomerDetailPage = lazyWithReload(
  () => import("@/pages/B2B/Receivables/CustomerDetail"),
);
const B2BGiftDebtsPage = lazyWithReload(() => import("@/pages/B2B/GiftDebts"));
const B2BCreditApprovalsPage = lazyWithReload(
  () => import("@/pages/B2B/CreditApprovals"),
);
const B2BReportsPage = lazyWithReload(() => import("@/pages/B2B/Reports"));
const B2BCrmPage = lazyWithReload(() => import("@/pages/B2B/CRM"));
const B2BCrmCustomersPage = lazyWithReload(
  () => import("@/pages/B2B/CRM/customers"),
);
const B2BCustomerNewPage = lazyWithReload(
  () => import("@/pages/B2B/CRM/customer-new"),
);
const B2BCustomer360Page = lazyWithReload(
  () => import("@/pages/B2B/CRM/customer-360"),
);
const B2BStaffInventoryPage = lazyWithReload(
  () => import("@/pages/B2B/StaffInventory"),
);
const B2BSettingsPage = lazyWithReload(() => import("@/pages/B2B/Settings"));

// Roles (NEW — /settings/roles) — TODO: EXTENSION BEYOND SITEMAP v1.1
const RolesPage = lazyWithReload(() =>
  import("@/pages/Roles").then((m) => ({ default: m.RolesPage })),
);
const RoleDetailPage = lazyWithReload(() =>
  import("@/pages/Roles/detail").then((m) => ({ default: m.RoleDetailPage })),
);

// Dashboard + Core gaps (Phase 1)
const DashboardPage = lazyWithReload(() =>
  import("@/pages/Dashboard").then((m) => ({ default: m.DashboardPage })),
);
const NotificationsPage = lazyWithReload(() =>
  import("@/pages/Notifications").then((m) => ({
    default: m.NotificationsPage,
  })),
);

// Customers CRM
const CustomersPage = lazyWithReload(() =>
  import("@/pages/Customers").then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazyWithReload(() =>
  import("@/pages/Customers/detail").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const CustomerPurchasesPage = lazyWithReload(() =>
  import("@/pages/Customers/purchases").then((m) => ({
    default: m.CustomerPurchasesPage,
  })),
);
const CustomerLoyaltyPage = lazyWithReload(() =>
  import("@/pages/Customers/loyalty").then((m) => ({
    default: m.CustomerLoyaltyPage,
  })),
);
const CustomerGroupsPage = lazyWithReload(() =>
  import("@/pages/Customers/groups").then((m) => ({
    default: m.CustomerGroupsPage,
  })),
);

// Auth flow
const OtpPage = lazyWithReload(() =>
  import("@/pages/Auth/Otp").then((m) => ({ default: m.OtpPage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import("@/pages/Auth/ForgotPassword").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import("@/pages/Auth/ResetPassword").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const RegisterPage = lazyWithReload(() =>
  import("@/pages/Auth/Register").then((m) => ({ default: m.RegisterPage })),
);
const SelectStorePage = lazyWithReload(() =>
  import("@/pages/Auth/SelectStore").then((m) => ({
    default: m.SelectStorePage,
  })),
);
const ChangePasswordPage = lazyWithReload(() =>
  import("@/pages/Auth/ChangePassword").then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
// EG-356 Sprint 6 (04b9120) — password-less signup → đặt password lần đầu sau OTP
const SetPasswordPage = lazyWithReload(() =>
  import("@/pages/Auth/SetPassword").then((m) => ({
    default: m.SetPasswordPage,
  })),
);

// POS enhancements
const PosCustomerDisplayPage = lazyWithReload(() =>
  import("@/pages/POS/CustomerDisplay").then((m) => ({
    default: m.CustomerDisplayPage,
  })),
);
const PosReturnsPage = lazyWithReload(() =>
  import("@/pages/POS/Returns").then((m) => ({ default: m.PosReturnsPage })),
);
const PosShiftsPage = lazyWithReload(() =>
  import("@/pages/POS/Shifts").then((m) => ({ default: m.ShiftsPage })),
);
const BuybackListPage = lazyWithReload(() =>
  import("@/pages/BuyBack").then((m) => ({ default: m.BuybackListPage })),
);
const BuybackNewPage = lazyWithReload(() =>
  import("@/pages/BuyBack/new").then((m) => ({ default: m.BuybackNewPage })),
);
const BuybackDetailPage = lazyWithReload(() =>
  import("@/pages/BuyBack/detail").then((m) => ({
    default: m.BuybackDetailPage,
  })),
);
const BuybackReportsPage = lazyWithReload(() =>
  import("@/pages/BuyBack/reports").then((m) => ({
    default: m.BuybackReportsPage,
  })),
);
const ExchangeListPage = lazyWithReload(() =>
  import("@/pages/Exchange").then((m) => ({ default: m.ExchangeListPage })),
);
const ExchangeNewPage = lazyWithReload(() =>
  import("@/pages/Exchange/new").then((m) => ({ default: m.ExchangeNewPage })),
);
const ExchangeDetailPage = lazyWithReload(() =>
  import("@/pages/Exchange/detail").then((m) => ({
    default: m.ExchangeDetailPage,
  })),
);
const ExchangeApprovePage = lazyWithReload(() =>
  import("@/pages/Exchange/approve").then((m) => ({
    default: m.ExchangeApprovePage,
  })),
);
const ExchangeReportsPage = lazyWithReload(() =>
  import("@/pages/Exchange/reports").then((m) => ({
    default: m.ExchangeReportsPage,
  })),
);

// Settings expansion
const ProfileSettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/Profile").then((m) => ({
    default: m.ProfileSettingsPage,
  })),
);
const SecuritySettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/Security").then((m) => ({
    default: m.SecuritySettingsPage,
  })),
);
const NotificationsSettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/Notifications").then((m) => ({
    default: m.NotificationsSettingsPage,
  })),
);
const GeneralSettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/General").then((m) => ({
    default: m.GeneralSettingsPage,
  })),
);
const LocaleSettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/Locale").then((m) => ({
    default: m.LocaleSettingsPage,
  })),
);
const DataExportPage = lazyWithReload(() =>
  import("@/pages/Settings/DataExport").then((m) => ({
    default: m.DataExportPage,
  })),
);

// Sales Invoices — EG-568, EG-569, EG-570, EG-571, EG-572
const GiftOrdersPage = lazyWithReload(() =>
  import("@/pages/Accounting/GiftOrders").then((m) => ({
    default: m.GiftOrdersPage,
  })),
);
const SalesInvoicesPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices").then((m) => ({
    default: m.SalesInvoicesPage,
  })),
);
const SalesInvoiceCreatePage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/create").then((m) => ({
    default: m.SalesInvoiceCreatePage,
  })),
);
const LegacySalesInvoiceEditEntryPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/legacy-edit-entry").then((m) => ({
    default: m.LegacySalesInvoiceEditEntryPage,
  })),
);
const SalesInvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/detail").then((m) => ({
    default: m.SalesInvoiceDetailPage,
  })),
);
const InvoiceManagementPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/invoice-management").then((m) => ({
    default: m.InvoiceManagementPage,
  })),
);
const SalesReturnDrawerPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/SalesReturnDrawer").then((m) => ({
    default: m.SalesReturnDrawerPage,
  })),
);
const SalesDiscountNewPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/discount-new").then((m) => ({
    default: m.SalesDiscountNewPage,
  })),
);
const DerivedSalesDocumentDetailPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/derived-document-detail").then((m) => ({
    default: m.DerivedSalesDocumentDetailPage,
  })),
);
const DerivedSalesDocumentEditPage = lazyWithReload(() =>
  import("@/pages/SalesInvoices/derived-document-edit").then((m) => ({
    default: m.DerivedSalesDocumentEditPage,
  })),
);

const SuppliersPage = lazyWithReload(() =>
  import("@/pages/Suppliers").then((m) => ({ default: m.SuppliersPage })),
);
const SupplierDetailPage = lazyWithReload(() =>
  import("@/pages/Suppliers/detail").then((m) => ({
    default: m.SupplierDetailPage,
  })),
);
const PurchasingPage = lazyWithReload(() =>
  import("@/pages/Purchasing").then((m) => ({ default: m.PurchasingPage })),
);
const PurchasingReceivingListPage = lazyWithReload(() =>
  import("@/pages/Purchasing/receiving-list").then((m) => ({
    default: m.ReceivingListPage,
  })),
);
const PurchasingDetailPage = lazyWithReload(() =>
  import("@/pages/Purchasing/detail").then((m) => ({
    default: m.PurchasingDetailPage,
  })),
);

const PromotionsPage = lazyWithReload(() =>
  import("@/pages/Promotions").then((m) => ({ default: m.PromotionsPage })),
);
const PromotionCreatePage = lazyWithReload(() =>
  import("@/pages/Promotions/create").then((m) => ({
    default: m.PromotionCreatePage,
  })),
);
const VouchersPage = lazyWithReload(() =>
  import("@/pages/Promotions/vouchers").then((m) => ({
    default: m.VouchersPage,
  })),
);
const VoucherCreatePage = lazyWithReload(() =>
  import("@/pages/Promotions/voucher-create").then((m) => ({
    default: m.VoucherCreatePage,
  })),
);
const FlashSalesPage = lazyWithReload(() =>
  import("@/pages/Promotions/flash-sales").then((m) => ({
    default: m.FlashSalesPage,
  })),
);
const FlashSaleCreatePage = lazyWithReload(() =>
  import("@/pages/Promotions/flash-sale-create").then((m) => ({
    default: m.FlashSaleCreatePage,
  })),
);
const LoyaltyPage = lazyWithReload(() =>
  import("@/pages/Promotions/loyalty").then((m) => ({
    default: m.LoyaltyPage,
  })),
);
const WholesaleGiftsPage = lazyWithReload(() =>
  import("@/pages/Promotions/wholesale-gifts").then((m) => ({
    default: m.WholesaleGiftsPage,
  })),
);
const ProductCombosPage = lazyWithReload(() =>
  import("@/pages/Products/combos").then((m) => ({
    default: m.ProductCombosPage,
  })),
);
const ProductComboNewPage = lazyWithReload(() =>
  import("@/pages/Products/combo-new").then((m) => ({
    default: m.ProductComboNewPage,
  })),
);
// TCR-1525 Task 6 — danh sách combo thật (is_combo=true, service sản phẩm), khác combo ảo ở trên.
const ProductCombosFixPage = lazyWithReload(() =>
  import("@/pages/Products/combos-fix").then((m) => ({
    default: m.ProductCombosFixPage,
  })),
);
// TCR-1525 Task 7 — form khai báo combo thật (4 tab), thay cho ProductComboNewPage
// (combo ảo) ở 2 route new/:id/edit bên dưới.
const ProductComboFixFormPage = lazyWithReload(() =>
  import("@/pages/Products/combo-fix-form").then((m) => ({
    default: m.ProductComboFixFormPage,
  })),
);
const PromotionReportsPage = lazyWithReload(() =>
  import("@/pages/Promotions/reports").then((m) => ({
    default: m.PromotionReportsPage,
  })),
);

const WorkshopPage = lazyWithReload(() =>
  import("@/pages/Workshop").then((m) => ({ default: m.WorkshopPage })),
);
const WorkshopDetailPage = lazyWithReload(() =>
  import("@/pages/Workshop/detail").then((m) => ({
    default: m.WorkshopDetailPage,
  })),
);
const CraftsmenPage = lazyWithReload(() =>
  import("@/pages/Workshop/craftsmen").then((m) => ({
    default: m.CraftsmenPage,
  })),
);
const WorkshopServicesPage = lazyWithReload(() =>
  import("@/pages/Workshop/services").then((m) => ({
    default: m.WorkshopServicesPage,
  })),
);

const OnboardingPage = lazyWithReload(() =>
  import("@/pages/Onboarding").then((m) => ({ default: m.OnboardingPage })),
);
const OnboardingCompletePage = lazyWithReload(() =>
  import("@/pages/Onboarding").then((m) => ({
    default: m.OnboardingCompletePage,
  })),
);
const OnboardingBusinessPage = lazyWithReload(() =>
  import("@/pages/Onboarding/business").then((m) => ({
    default: m.OnboardingBusinessPage,
  })),
);
const OnboardingStorePage = lazyWithReload(() =>
  import("@/pages/Onboarding/store").then((m) => ({
    default: m.OnboardingStorePage,
  })),
);
const OnboardingStaffPage = lazyWithReload(() =>
  import("@/pages/Onboarding/staff").then((m) => ({
    default: m.OnboardingStaffPage,
  })),
);
const OnboardingProductsPage = lazyWithReload(() =>
  import("@/pages/Onboarding/products").then((m) => ({
    default: m.OnboardingProductsPage,
  })),
);
const OnboardingPaymentPage = lazyWithReload(() =>
  import("@/pages/Onboarding/payment").then((m) => ({
    default: m.OnboardingPaymentPage,
  })),
);
const OnboardingInvoicePage = lazyWithReload(() =>
  import("@/pages/Onboarding/invoice").then((m) => ({
    default: m.OnboardingInvoicePage,
  })),
);

const SetupIndexPage = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.SetupIndexPage })),
);
const SetupWizardLayout = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.SetupWizardLayout })),
);
const SetupBrandingStep = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.BrandingStep })),
);
const SetupCatalogStep = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.CatalogStep })),
);
const SetupPluginStep = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.PluginStep })),
);
const SetupHardwareStep = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.HardwareStep })),
);
const SetupDemoStep = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.DemoStep })),
);
const SetupCompletePage = lazyWithReload(() =>
  import("@/pages/Setup").then((m) => ({ default: m.SetupCompletePage })),
);

// Phase 2b — Pawn, Installment, Consignment, Labels, Finance
const PawnListPage = lazyWithReload(() =>
  import("@/pages/Pawn").then((m) => ({ default: m.PawnListPage })),
);
const PawnDetailPage = lazyWithReload(() =>
  import("@/pages/Pawn/detail").then((m) => ({ default: m.PawnDetailPage })),
);
const PawnDueTodayPage = lazyWithReload(() =>
  import("@/pages/Pawn/due").then((m) => ({ default: m.PawnDueTodayPage })),
);
const PawnOverduePage = lazyWithReload(() =>
  import("@/pages/Pawn/due").then((m) => ({ default: m.PawnOverduePage })),
);

const InstallmentListPage = lazyWithReload(() =>
  import("@/pages/Installment").then((m) => ({
    default: m.InstallmentListPage,
  })),
);
const InstallmentDetailPage = lazyWithReload(() =>
  import("@/pages/Installment/detail").then((m) => ({
    default: m.InstallmentDetailPage,
  })),
);

const ConsignmentListPage = lazyWithReload(() =>
  import("@/pages/Consignment").then((m) => ({
    default: m.ConsignmentListPage,
  })),
);
const ConsignmentDetailPage = lazyWithReload(() =>
  import("@/pages/Consignment/detail").then((m) => ({
    default: m.ConsignmentDetailPage,
  })),
);

const LabelsPage = lazyWithReload(() =>
  import("@/pages/Labels").then((m) => ({ default: m.LabelsPage })),
);
const LabelEditorPage = lazyWithReload(() =>
  import("@/pages/Labels/editor").then((m) => ({ default: m.LabelEditorPage })),
);
const LabelPrintBatchPage = lazyWithReload(() =>
  import("@/pages/Labels/print").then((m) => ({
    default: m.LabelPrintBatchPage,
  })),
);

const FinanceCashBookPage = lazyWithReload(() =>
  import("@/pages/Finance/cash-book").then((m) => ({
    default: m.FinanceCashBookPage,
  })),
);
const FinanceVouchersPage = lazyWithReload(() =>
  import("@/pages/Finance/vouchers").then((m) => ({
    default: m.FinanceVouchersPage,
  })),
);
const FinanceInvoicesPage = lazyWithReload(() =>
  import("@/pages/EInvoice").then((m) => ({ default: m.EinvoiceListPage })),
);

// Phase 3 — Online Orders, Multi-store, Reports advanced, Import, Audit, Support

const StoresListPage = lazyWithReload(() =>
  import("@/pages/Stores").then((m) => ({ default: m.StoresListPage })),
);
const StoresEditPage = lazyWithReload(() =>
  import("@/pages/Stores/edit").then((m) => ({ default: m.StoresEditPage })),
);
const StoreHardwarePage = lazyWithReload(() =>
  import("@/pages/Stores/hardware").then((m) => ({
    default: m.StoreHardwarePage,
  })),
);

const ProfitReportPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/profit").then((m) => ({
    default: m.ProfitReportPage,
  })),
);
const StaffReportPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/staff").then((m) => ({
    default: m.StaffReportPage,
  })),
);
const CustomersReportPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/customers").then((m) => ({
    default: m.CustomersReportPage,
  })),
);
const MultiStoreReportPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/multi-store").then((m) => ({
    default: m.MultiStoreReportPage,
  })),
);
const ReportBuilderPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/builder").then((m) => ({
    default: m.ReportBuilderPage,
  })),
);

const ImportHubPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportHubPage })),
);
const ImportJobDetailPage = lazyWithReload(() =>
  import("@/pages/Import/JobDetail").then((m) => ({
    default: m.ImportJobDetailPage,
  })),
);
const ImportProductsPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportProductsPage })),
);
const ImportCustomersPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportCustomersPage })),
);
const ImportSuppliersPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportSuppliersPage })),
);
const ImportEmployeesPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportEmployeesPage })),
);
const ImportContractsPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportContractsPage })),
);
const ImportBenefitsPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportBenefitsPage })),
);
const ImportPayrollsPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportPayrollsPage })),
);
const ImportInsurancesPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportInsurancesPage })),
);
const ImportInsuranceRosterPage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({
    default: m.ImportInsuranceRosterPage,
  })),
);
const ImportAttendancePage = lazyWithReload(() =>
  import("@/pages/Import").then((m) => ({ default: m.ImportAttendancePage })),
);

// EG-345 P4 — moved from /pages/Audit/* mocks → /pages/Settings/* (BE wired)
const AuditLogPage = lazyWithReload(() =>
  import("@/pages/Settings/AuditLog").then((m) => ({
    default: m.AuditLogPage,
  })),
);
const MigrationLogPage = lazyWithReload(() =>
  import("@/pages/Settings/MigrationLog").then((m) => ({
    default: m.MigrationLogPage,
  })),
);
const WorkerMonitorPage = lazyWithReload(() =>
  import("@/pages/Settings/WorkerMonitor").then((m) => ({
    default: m.WorkerMonitorPage,
  })),
);
// TCR-2653 — trang Giám sát hệ thống (nivo) tách chunk riêng, chỉ tải khi vào trang.
const OpsDashboardPage = lazyWithReload(() =>
  import("@/pages/Settings/OpsDashboard").then((m) => ({
    default: m.OpsDashboardPage,
  })),
);
const SystemLogsPage = lazyWithReload(() =>
  import("@/pages/SystemLogs").then((m) => ({ default: m.SystemLogsPage })),
);
const ApiKeysPage = lazyWithReload(() =>
  import("@/pages/Settings/ApiKeys").then((m) => ({ default: m.ApiKeysPage })),
);

// TCR-1743 — SupportHomePage KHÔNG còn được route tới (ẩn hẳn Trung tâm hỗ trợ). File
// `@/pages/Support/index.tsx` vẫn nằm trong repo để bật lại nhanh, nhưng không import ⇒ không
// vào bundle. Muốn bật lại: khôi phục lazy import này + <Route index> bên dưới + mục sidebar.
const ChangelogPage = lazyWithReload(() =>
  import("@/pages/Support/changelog").then((m) => ({
    default: m.ChangelogPage,
  })),
);

// Phase 4a — P0 Create forms
const PawnNewPage = lazyWithReload(() =>
  import("@/pages/Pawn/new").then((m) => ({ default: m.PawnNewPage })),
);
const InstallmentNewPage = lazyWithReload(() =>
  import("@/pages/Installment/new").then((m) => ({
    default: m.InstallmentNewPage,
  })),
);
const InstallmentCollectionsPage = lazyWithReload(() =>
  import("@/pages/Installment/due").then((m) => ({
    default: m.InstallmentCollectionsPage,
  })),
);
const InstallmentCollectPage = lazyWithReload(() =>
  import("@/pages/Installment/collect").then((m) => ({
    default: m.InstallmentCollectPage,
  })),
);
const InstallmentSchedulePage = lazyWithReload(() =>
  import("@/pages/Installment/schedule").then((m) => ({
    default: m.InstallmentSchedulePage,
  })),
);
const ConsignmentNewPage = lazyWithReload(() =>
  import("@/pages/Consignment/new").then((m) => ({
    default: m.ConsignmentNewPage,
  })),
);
const SupplierFormPage = lazyWithReload(() =>
  import("@/pages/Suppliers/form").then((m) => ({
    default: m.SupplierFormPage,
  })),
);
const PurchasingNewPage = lazyWithReload(() =>
  import("@/pages/Purchasing/new").then((m) => ({
    default: m.PurchasingNewPage,
  })),
);
const PurchasingReceivePage = lazyWithReload(() =>
  import("@/pages/Purchasing/receive").then((m) => ({
    default: m.PurchasingReceivePage,
  })),
);
const PurchasingMatchPage = lazyWithReload(() =>
  import("@/pages/Purchasing/match").then((m) => ({
    default: m.PurchasingMatchPage,
  })),
);
const WorkshopNewPage = lazyWithReload(() =>
  import("@/pages/Workshop/new").then((m) => ({ default: m.WorkshopNewPage })),
);
const WorkshopCommissionsPage = lazyWithReload(() =>
  import("@/pages/Workshop/commissions").then((m) => ({
    default: m.WorkshopCommissionsPage,
  })),
);
const CustomerNewPage = lazyWithReload(() =>
  import("@/pages/Customers/new").then((m) => ({ default: m.CustomerNewPage })),
);
const CustomerEditPage = lazyWithReload(() =>
  import("@/pages/Customers/edit").then((m) => ({
    default: m.CustomerEditPage,
  })),
);
const CustomerCreditsPage = lazyWithReload(() =>
  import("@/pages/Customers/credits").then((m) => ({
    default: m.CustomerCreditsPage,
  })),
);
const SupplierHistoryPage = lazyWithReload(() =>
  import("@/pages/Suppliers/history").then((m) => ({
    default: m.SupplierHistoryPage,
  })),
);
const PawnSettingsPage = lazyWithReload(() =>
  import("@/pages/Pawn/settings").then((m) => ({
    default: m.PawnSettingsPage,
  })),
);
const InstallmentSettingsPage = lazyWithReload(() =>
  import("@/pages/Installment/settings").then((m) => ({
    default: m.InstallmentSettingsPage,
  })),
);
const EinvoiceSettingsPage = lazyWithReload(() =>
  import("@/pages/EInvoice/settings").then((m) => ({
    default: m.EinvoiceSettingsPage,
  })),
);
const EinvoiceListPage = lazyWithReload(() =>
  import("@/pages/EInvoice").then((m) => ({ default: m.EinvoiceListPage })),
);
const EinvoiceNewPage = lazyWithReload(() =>
  import("@/pages/EInvoice/new").then((m) => ({ default: m.EinvoiceNewPage })),
);
const EinvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/EInvoice/detail").then((m) => ({
    default: m.EinvoiceDetailPage,
  })),
);
const InvoiceLookupPage = lazyWithReload(() =>
  import("@/pages/public/InvoiceLookup").then((m) => ({
    default: m.InvoiceLookupPage,
  })),
);
// TCR-1270 — Public referral landing (khách chưa đăng nhập bấm link /ref/{code} chia sẻ từ trang Referral)
const ReferralLandingPage = lazyWithReload(() =>
  import("@/pages/public/ReferralLanding").then((m) => ({
    default: m.ReferralLandingPage,
  })),
);
const EinvoiceStatsPage = lazyWithReload(() =>
  import("@/pages/EInvoice/stats").then((m) => ({
    default: m.EinvoiceStatsPage,
  })),
);
const EinvoiceTaxReportPage = lazyWithReload(() =>
  import("@/pages/EInvoice/tax-report").then((m) => ({
    default: m.EinvoiceTaxReportPage,
  })),
);
const PaymentMethodsPage = lazyWithReload(() =>
  import("@/pages/Finance/payment-methods").then((m) => ({
    default: m.PaymentMethodsPage,
  })),
);
const LabelsPrinterSettingsPage = lazyWithReload(() =>
  import("@/pages/Labels/printer-settings").then((m) => ({
    default: m.LabelsPrinterSettingsPage,
  })),
);
const ProductUnitsPage = lazyWithReload(() =>
  import("@/pages/Products/units").then((m) => ({
    default: m.ProductUnitsPage,
  })),
);
const SettingsIndexPage = lazyWithReload(() =>
  import("@/pages/Settings").then((m) => ({ default: m.SettingsIndexPage })),
);

// EG-345 — Business (Settings namespace). Payment methods + E-invoice settings
// nằm ở /finance/* (đã có sẵn) — Settings sidebar chỉ link sang.
const BusinessSettingsPage = lazyWithReload(() =>
  import("@/pages/Settings/Business").then((m) => ({
    default: m.BusinessSettingsPage,
  })),
);
const ReceiptTemplateListPage = lazyWithReload(() =>
  import("@/pages/Settings/ReceiptTemplate").then((m) => ({
    default: m.ReceiptTemplateListPage,
  })),
);
const ReceiptTemplateEditorPage = lazyWithReload(() =>
  import("@/pages/Settings/ReceiptTemplate/editor").then((m) => ({
    default: m.ReceiptTemplateEditorPage,
  })),
);
// EG-508 — Payroll settings
const PayrollSettingsPage = lazyWithReload(() =>
  import("@/pages/Payroll/settings").then((m) => ({
    default: m.PayrollSettingsPage,
  })),
);
// EG-509 — Payroll components
const PayrollComponentsPage = lazyWithReload(() =>
  import("@/pages/Payroll/components").then((m) => ({
    default: m.PayrollComponentsPage,
  })),
);
// EG-517 — Payroll runs
const PayrollRunsPage = lazyWithReload(() =>
  import("@/pages/Payroll/runs").then((m) => ({
    default: m.PayrollRunListPage,
  })),
);
// EG-518 — Payroll run detail
const PayrollRunDetailPage = lazyWithReload(() =>
  import("@/pages/Payroll/run-detail").then((m) => ({
    default: m.PayrollRunDetailPage,
  })),
);
// EG-525 — Payroll summary dashboard
const PayrollSummaryDashboard = lazyWithReload(() =>
  import("@/pages/Payroll/dashboard").then((m) => ({
    default: m.PayrollDashboardPage,
  })),
);
// EG-526 — S5-HKD payroll register report
const S5HKDPage = lazyWithReload(() =>
  import("@/pages/Payroll/reports/s5-hkd").then((m) => ({
    default: m.S5HKDReportPage,
  })),
);
// EG-527 — TNCN Quyết toán Mẫu 02 report
const QTTTNcnPage = lazyWithReload(() =>
  import("@/pages/Payroll/reports/qtt-tncn").then((m) => ({
    default: m.QttTncnPage,
  })),
);
// EG-348 Phase D — CRM 360 follow-ups board
const FollowUpsBoardPage = lazyWithReload(() =>
  import("@/pages/CRM/follow-ups").then((m) => ({
    default: m.FollowUpsBoardPage,
  })),
);
const CrmDashboardPage = lazyWithReload(() =>
  import("@/pages/CRM/dashboard").then((m) => ({
    default: m.CrmDashboardPage,
  })),
);
// EG-375 — Referral Program (BE EG-335)
const ReferralOverviewPage = lazyWithReload(() =>
  import("@/pages/Referral").then((m) => ({ default: m.ReferralOverviewPage })),
);
const ReferralListPage = lazyWithReload(() =>
  import("@/pages/Referral/list").then((m) => ({
    default: m.ReferralListPage,
  })),
);
const ReferralLeaderboardPage = lazyWithReload(() =>
  import("@/pages/Referral/leaderboard").then((m) => ({
    default: m.ReferralLeaderboardPage,
  })),
);
const ReferralConfigPage = lazyWithReload(() =>
  import("@/pages/Referral/config").then((m) => ({
    default: m.ReferralConfigPage,
  })),
);
const PawnReportsPage = lazyWithReload(() =>
  import("@/pages/Pawn/reports").then((m) => ({ default: m.PawnReportsPage })),
);
const InstallmentReportsPage = lazyWithReload(() =>
  import("@/pages/Installment/reports").then((m) => ({
    default: m.InstallmentReportsPage,
  })),
);
const ConsignmentReportsPage = lazyWithReload(() =>
  import("@/pages/Consignment/reports").then((m) => ({
    default: m.ConsignmentReportsPage,
  })),
);
const WorkshopReportsPage = lazyWithReload(() =>
  import("@/pages/Workshop/reports").then((m) => ({
    default: m.WorkshopReportsPage,
  })),
);
const PurchasingReportsPage = lazyWithReload(() =>
  import("@/pages/Purchasing/reports").then((m) => ({
    default: m.PurchasingReportsPage,
  })),
);
const ApSummaryPage = lazyWithReload(() =>
  import("@/pages/Purchasing/reports/ap-summary").then((m) => ({
    default: m.ApSummaryPage,
  })),
);
const ApDetailPage = lazyWithReload(() =>
  import("@/pages/Purchasing/reports/ap-detail").then((m) => ({
    default: m.ApDetailPage,
  })),
);
const PurchaseLedgerPage = lazyWithReload(() =>
  import("@/pages/Purchasing/reports/purchase-ledger").then((m) => ({
    default: m.PurchaseLedgerPage,
  })),
);
const ProductsReportPage = lazyWithReload(() =>
  import("@/pages/Reports/advanced/products").then((m) => ({
    default: m.ProductsReportPage,
  })),
);
const FinanceInvoiceIssuePage = lazyWithReload(() =>
  import("@/pages/Finance/invoices-issue").then((m) => ({
    default: m.FinanceInvoiceIssuePage,
  })),
);
const FinanceInvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/Finance/invoice-detail").then((m) => ({
    default: m.FinanceInvoiceDetailPage,
  })),
);
const FinanceInvoiceAdjustPage = lazyWithReload(() =>
  import("@/pages/Finance/invoice-adjust").then((m) => ({
    default: m.FinanceInvoiceAdjustPage,
  })),
);
const LabelPreviewPage = lazyWithReload(() =>
  import("@/pages/Labels/preview").then((m) => ({
    default: m.LabelPreviewPage,
  })),
);
// Inventory Phase 4d
const GoodsReceiptListPage = lazyWithReload(() =>
  import("@/pages/Inventory/receipts/list").then((m) => ({
    default: m.GoodsReceiptListPage,
  })),
);
const GoodsReceiptNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/receipts/new").then((m) => ({
    default: m.GoodsReceiptNewPage,
  })),
);
const GoodsReceiptDetailPage = lazyWithReload(() =>
  import("@/pages/Inventory/receipts/detail").then((m) => ({
    default: m.GoodsReceiptDetailPage,
  })),
);
const GoodsReceiptEditPage = lazyWithReload(() =>
  import("@/pages/Inventory/receipts/edit").then((m) => ({
    default: m.GoodsReceiptEditPage,
  })),
);
const GoodsIssueListPage = lazyWithReload(() =>
  import("@/pages/Inventory/issues/list").then((m) => ({
    default: m.GoodsIssueListPage,
  })),
);
// TCR-1517 Task 7 — Lệnh lắp ráp / tháo dỡ combo thật.
const AssemblyOrdersPage = lazyWithReload(() =>
  import("@/pages/Inventory/AssemblyOrders").then((m) => ({
    default: m.AssemblyOrdersPage,
  })),
);
const GoodsIssueNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/issues/new").then((m) => ({
    default: m.GoodsIssueNewPage,
  })),
);
const GoodsIssueDetailPage = lazyWithReload(() =>
  import("@/pages/Inventory/issues/detail").then((m) => ({
    default: m.GoodsIssueDetailPage,
  })),
);
const GoodsIssueEditPage = lazyWithReload(() =>
  import("@/pages/Inventory/issues/edit").then((m) => ({
    default: m.GoodsIssueEditPage,
  })),
);
const StockTransferListPage = lazyWithReload(() =>
  import("@/pages/Inventory/transfers/list").then((m) => ({
    default: m.StockTransferListPage,
  })),
);
const StockTransferNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/transfers/new").then((m) => ({
    default: m.StockTransferNewPage,
  })),
);
const StockTransferDetailPage = lazyWithReload(() =>
  import("@/pages/Inventory/transfers/detail").then((m) => ({
    default: m.StockTransferDetailPage,
  })),
);
const StockTransferEditPage = lazyWithReload(() =>
  import("@/pages/Inventory/transfers/edit").then((m) => ({
    default: m.StockTransferEditPage,
  })),
);
const StockCountListPage = lazyWithReload(() =>
  import("@/pages/Inventory/counts/list").then((m) => ({
    default: m.StockCountListPage,
  })),
);
const StockCountNewPage = lazyWithReload(() =>
  import("@/pages/Inventory/counts/new").then((m) => ({
    default: m.StockCountNewPage,
  })),
);
const StockCountEnterPage = lazyWithReload(() =>
  import("@/pages/Inventory/counts/enter").then((m) => ({
    default: m.StockCountEnterPage,
  })),
);
const StockCountVariancePage = lazyWithReload(() =>
  import("@/pages/Inventory/counts/variance").then((m) => ({
    default: m.StockCountVariancePage,
  })),
);
const InventoryMovementsPage = lazyWithReload(() =>
  import("@/pages/Inventory/movements").then((m) => ({
    default: m.InventoryMovementsPage,
  })),
);
const InventoryAlertsPage = lazyWithReload(() =>
  import("@/pages/Inventory/alerts").then((m) => ({
    default: m.InventoryAlertsPage,
  })),
);
const InventoryReportsPage4d = lazyWithReload(() =>
  import("@/pages/Inventory/reports").then((m) => ({
    default: m.InventoryReportsPage,
  })),
);
const InventoryPrintLabelsPage = lazyWithReload(() =>
  import("@/pages/Inventory/print-labels").then((m) => ({
    default: m.InventoryPrintLabelsPage,
  })),
);
const StoreNewPage = lazyWithReload(() =>
  import("@/pages/Stores/new").then((m) => ({ default: m.StoreNewPage })),
);
const StaffInvitePage = lazyWithReload(() =>
  import("@/pages/Staffs/invite").then((m) => ({ default: m.StaffInvitePage })),
);
const StaffActivityPage = lazyWithReload(() =>
  import("@/pages/Staffs/activity").then((m) => ({
    default: m.StaffActivityPage,
  })),
);
const StaffStoresPage = lazyWithReload(() =>
  import("@/pages/Staffs/stores").then((m) => ({ default: m.StaffStoresPage })),
);
const CategoriesPage = lazyWithReload(() =>
  import("@/pages/Products/categories").then((m) => ({
    default: m.CategoriesPage,
  })),
);
const MetalCategoriesPage = lazyWithReload(() =>
  import("@/pages/Products/metal-categories").then((m) => ({
    default: m.MetalCategoriesPage,
  })),
);
const BrandsPage = lazyWithReload(() =>
  import("@/pages/Products/brands").then((m) => ({ default: m.BrandsPage })),
);
const AttributesPage = lazyWithReload(() =>
  import("@/pages/Products/attributes").then((m) => ({
    default: m.AttributesPage,
  })),
);

// Commission — EG-350
const CommissionDashboardPage = lazyWithReload(() =>
  import("@/pages/Commission").then((m) => ({
    default: m.CommissionDashboardPage,
  })),
);
const CommissionMyPage = lazyWithReload(() =>
  import("@/pages/Commission/my").then((m) => ({
    default: m.CommissionMyPage,
  })),
);
const CommissionKpiPage = lazyWithReload(() =>
  import("@/pages/Commission/kpi").then((m) => ({
    default: m.CommissionKpiPage,
  })),
);
const CommissionLeaderboardPage = lazyWithReload(() =>
  import("@/pages/Commission/leaderboard").then((m) => ({
    default: m.CommissionLeaderboardPage,
  })),
);
const CommissionPayoutsPage = lazyWithReload(() =>
  import("@/pages/Commission/payouts").then((m) => ({
    default: m.CommissionPayoutsPage,
  })),
);
// EG-350a/b/c — Phase 2 admin pages
const CommissionKpiTargetsPage = lazyWithReload(() =>
  import("@/pages/Commission/kpi-targets").then((m) => ({
    default: m.CommissionKpiTargetsPage,
  })),
);
const CommissionRolesPage = lazyWithReload(() =>
  import("@/pages/Commission/config/roles").then((m) => ({
    default: m.CommissionRolesPage,
  })),
);
const CommissionProductRatesPage = lazyWithReload(() =>
  import("@/pages/Commission/config/product-rates").then((m) => ({
    default: m.ProductRatesPage,
  })),
);
// EG-350d — Phase 3 general config wizard
const CommissionGeneralConfigPage = lazyWithReload(() =>
  import("@/pages/Commission/config/general").then((m) => ({
    default: m.CommissionGeneralConfigPage,
  })),
);
// EG-350e/g — Phase 4/6 admin pages
const CommissionLiveSessionsPage = lazyWithReload(() =>
  import("@/pages/Commission/live-sessions").then((m) => ({
    default: m.CommissionLiveSessionsPage,
  })),
);
const CommissionReportsPage = lazyWithReload(() =>
  import("@/pages/Commission/reports").then((m) => ({
    default: m.CommissionReportsPage,
  })),
);
// EG-350f — Phase 5 review drill-down
const CommissionReviewIndexPage = lazyWithReload(() =>
  import("@/pages/Commission/review/index").then((m) => ({
    default: m.CommissionReviewIndexPage,
  })),
);
const CommissionReviewDetailPage = lazyWithReload(() =>
  import("@/pages/Commission/review/detail").then((m) => ({
    default: m.CommissionReviewDetailPage,
  })),
);
// P3.1 — Bonus thresholds
const CommissionBonusThresholdsPage = lazyWithReload(() =>
  import("@/pages/Commission/config/bonus-thresholds").then((m) => ({
    default: m.BonusThresholdsPage,
  })),
);
// P3.2 — Adjustments + Audit log
const CommissionAdjustmentsPage = lazyWithReload(() =>
  import("@/pages/Commission/adjustments").then((m) => ({
    default: m.CommissionAdjustmentsPage,
  })),
);

// EG-520 — Payslip history (self-service)
const PayslipHistoryPage = lazyWithReload(() =>
  import("@/pages/Payroll/payslips").then((m) => ({ default: m.PayslipsPage })),
);

// Shipping — EG-351
const ShippingListPage = lazyWithReload(() =>
  import("@/pages/Shipping").then((m) => ({ default: m.ShippingListPage })),
);
const ShippingNewPage = lazyWithReload(() =>
  import("@/pages/Shipping/new").then((m) => ({ default: m.ShippingNewPage })),
);
const ShippingDetailPage = lazyWithReload(() =>
  import("@/pages/Shipping/detail").then((m) => ({
    default: m.ShippingDetailPage,
  })),
);
const PickupLocationsPage = lazyWithReload(() =>
  import("@/pages/Shipping/pickup-locations").then((m) => ({
    default: m.PickupLocationsPage,
  })),
);
const GhtkLogsPage = lazyWithReload(() =>
  import("@/pages/Shipping/ghtk-logs").then((m) => ({
    default: m.GhtkLogsPage,
  })),
);
const ShippingLogsPage = lazyWithReload(() =>
  import("@/pages/Shipping/logs").then((m) => ({
    default: m.ShippingLogsPage,
  })),
);
const CodReconciliationPage = lazyWithReload(() =>
  import("@/pages/Shipping/cod-reconciliation").then((m) => ({
    default: m.CodReconciliationPage,
  })),
);
const CodReconciliationDetailPage = lazyWithReload(() =>
  import("@/pages/Shipping/cod-reconciliation-detail").then((m) => ({
    default: m.CodReconciliationDetailPage,
  })),
);
const CodRemittancesPage = lazyWithReload(() =>
  import("@/pages/Shipping/cod-remittances").then((m) => ({
    default: m.CodRemittancesPage,
  })),
);

// Accounting Settings — S4.1 FE Accounting
const AccountingSettingsPage = lazyWithReload(() =>
  import("@/pages/AccountingSettings").then((m) => ({
    default: m.AccountingSettingsPage,
  })),
);
const AmisSyncPage = lazyWithReload(() =>
  import("@/modules/accounting-sync/pages/AmisSync").then((m) => ({
    default: m.AmisSyncPage,
  })),
);
const AmisMasterdataDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingSettings/masterdata/detail").then((m) => ({
    default: m.MasterdataDetailPage,
  })),
);
const AmisOutboxDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingSettings/masterdata/outbox/detail").then((m) => ({
    default: m.AmisOutboxDetailPage,
  })),
);
const AccountingReportsPage = lazyWithReload(() =>
  import("@/pages/AccountingReports").then((m) => ({
    default: m.AccountingReportsPage,
  })),
);
const BusinessResultsReportPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/business-results").then((m) => ({
    default: m.BusinessResultsReportPage,
  })),
);
const BalanceSheetReportPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/balance-sheet").then((m) => ({
    default: m.BalanceSheetReportPage,
  })),
);
const TrialBalanceReportPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/trial-balance").then((m) => ({
    default: m.TrialBalanceReportPage,
  })),
);
const S2bRevenueLedgerPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/s2b-revenue-ledger").then((m) => ({
    default: m.S2bRevenueLedgerPage,
  })),
);
const S2cIncomeExpenseDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/s2c-income-expense-detail").then((m) => ({
    default: m.S2cIncomeExpenseDetailPage,
  })),
);
const S2dMaterialsDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/s2d-materials-detail").then((m) => ({
    default: m.S2dMaterialsDetailPage,
  })),
);
const S2eCashDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/s2e-cash-detail").then((m) => ({
    default: m.S2eCashDetailPage,
  })),
);
const InventorySummaryReportPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/inventory-summary").then((m) => ({
    default: m.InventorySummaryReportPage,
  })),
);
const CashBookPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/cash-book").then((m) => ({
    default: m.CashBookPage,
  })),
);
const BankDepositBookPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/bank-deposit-book").then((m) => ({
    default: m.BankDepositBookPage,
  })),
);
const JournalEntriesReportPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/journal-entries").then((m) => ({
    default: m.JournalEntriesReportPage,
  })),
);
const JournalEntryDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/journal-entry-detail").then((m) => ({
    default: m.JournalEntryDetailPage,
  })),
);
const DocumentListingPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/document-listing").then((m) => ({
    default: m.DocumentListingPage,
  })),
);
const VoucherListingPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/voucher-listing").then((m) => ({
    default: m.VoucherListingPage,
  })),
);
const InvoiceListingPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/invoice-listing").then((m) => ({
    default: m.InvoiceListingPage,
  })),
);
const WarehouseVoucherListingPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/warehouse-voucher-listing").then((m) => ({
    default: m.WarehouseVoucherListingPage,
  })),
);
const ReceivablePayableListingPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/receivable-payable-listing").then((m) => ({
    default: m.ReceivablePayableListingPage,
  })),
);
const ArSummaryPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/ar-summary").then((m) => ({
    default: m.ArSummaryPage,
  })),
);
const ArDetailPage = lazyWithReload(() =>
  import("@/pages/AccountingReports/ar-detail").then((m) => ({
    default: m.ArDetailPage,
  })),
);
const AccountingEInvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/Accounting/EInvoices/detail").then((m) => ({
    default: m.AccountingEInvoiceDetailPage,
  })),
);
const EInvoiceListPage = lazyWithReload(() =>
  import("@/pages/Accounting/EInvoices/list").then((m) => ({
    default: m.EInvoiceListPage,
  })),
);
const EInvoiceFormPage = lazyWithReload(() =>
  import("@/pages/Accounting/EInvoices/new-entry").then((m) => ({
    default: m.EInvoiceCreateEntryPage,
  })),
);
const EInvoiceReportPage = lazyWithReload(() =>
  import("@/pages/Accounting/EInvoices/report").then((m) => ({
    default: m.EInvoiceReportPage,
  })),
);
const SalesSummaryByProductPage = lazyWithReload(() =>
  import("@/pages/Sales/reports/sales-summary-by-product").then((m) => ({
    default: m.SalesSummaryByProductPage,
  })),
);
const SalesDetailPage = lazyWithReload(() =>
  import("@/pages/Sales/reports/sales-detail").then((m) => ({
    default: m.SalesDetailPage,
  })),
);

// Accounting Dashboard — EG-605
const DashboardOverviewPage = lazyWithReload(
  () => import("@/pages/Accounting/dashboard-overview"),
);

// Accounting Transactions — S4.1 FE Accounting (EG-583 to EG-588)
const TransactionsListPage = lazyWithReload(() => import('@/pages/Accounting/transactions-list').then((m) => ({ default: m.TransactionsListPage })));
const BankDepositsPage = lazyWithReload(() => import('@/pages/Accounting/bank-deposits').then((m) => ({ default: m.BankDepositsPage })));
const ReceiptFormPage = lazyWithReload(() => import('@/pages/Accounting/receipt-form').then((m) => ({ default: m.ReceiptFormPage })));
const PaymentFormPage = lazyWithReload(() => import('@/pages/Accounting/payment-form').then((m) => ({ default: m.PaymentFormPage })));
const InternalTransferFormPage = lazyWithReload(() => import('@/pages/Accounting/internal-transfer-form'));
const PrepaidExpensesPage = lazyWithReload(() => import('@/pages/Accounting/prepaid-expenses'));
const FixedAssetGroupsPage = lazyWithReload(() => import('@/pages/Accounting/fixed-asset-groups'));
const FixedAssetsPage = lazyWithReload(() => import('@/pages/Accounting/fixed-assets'));
const FixedAssetDetailPage = lazyWithReload(() => import('@/pages/Accounting/fixed-asset-detail'));
const ToolSuppliesPage = lazyWithReload(() => import('@/pages/Accounting/tool-supplies'));
const ToolSupplyFormPage = lazyWithReload(() => import('@/pages/Accounting/tool-supply-form'));
const ToolSupplyReportsPage = lazyWithReload(() => import('@/pages/Accounting/tool-supply-reports'));
const ToolSupplyDetailPage = lazyWithReload(() => import('@/pages/Accounting/tool-supply-detail'));
const FixedAssetFormPage = lazyWithReload(() => import('@/pages/Accounting/fixed-asset-form'));
const PrepaidExpenseFormPage = lazyWithReload(() => import('@/pages/Accounting/prepaid-expense-form'));
const OtherDocumentPage = lazyWithReload(() => import('@/pages/Accounting/other-document'));
const AccountingDataRepairPage = lazyWithReload(() => import('@/pages/Accounting/data-repair'));
const DocumentScenarioTestPage = lazyWithReload(() => import('@/pages/Accounting/DocumentScenarios/test'));
const DocumentScenariosPage = lazyWithReload(() => import('@/pages/Accounting/DocumentScenarios'));

// Purchasing Accounting (EG-575 to EG-577)
const PurchaseInvoicesPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-invoices").then((m) => ({
    default: m.PurchaseInvoicesPage,
  })),
);
const PurchaseInvoiceNewPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-invoice-new").then((m) => ({
    default: m.PurchaseInvoiceNewPage,
  })),
);
const PurchaseInvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-invoice-detail").then((m) => ({
    default: m.PurchaseInvoiceDetailPage,
  })),
);
const PurchaseInvoiceImportNewPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-invoice-import-new").then((m) => ({
    default: m.PurchaseInvoiceImportNewPage,
  })),
);
const PurchaseReturnsPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-returns").then((m) => ({
    default: m.PurchaseReturnsPage,
  })),
);
const PurchaseReturnNewPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-returns-new").then((m) => ({
    default: m.PurchaseReturnNewPage,
  })),
);
const PurchaseReturnDetailPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-return-detail").then((m) => ({
    default: m.PurchaseReturnDetailPage,
  })),
);
const PurchaseDiscountsPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-discounts").then((m) => ({
    default: m.PurchaseDiscountsPage,
  })),
);
const PurchaseDiscountNewPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-discounts-new").then((m) => ({
    default: m.PurchaseDiscountNewPage,
  })),
);
const PurchaseDiscountDetailPage = lazyWithReload(() =>
  import("@/pages/Purchasing/purchase-discount-detail").then((m) => ({
    default: m.PurchaseDiscountDetailPage,
  })),
);

// EInvoice Accounting (EG-578)
const GovInvoicesPage = lazyWithReload(() =>
  import("@/pages/EInvoice/gov-invoices").then((m) => ({
    default: m.GovInvoicesPage,
  })),
);
const GovInvoiceDetailPage = lazyWithReload(() =>
  import("@/pages/EInvoice/gov-invoice-detail").then((m) => ({
    default: m.GovInvoiceDetailPage,
  })),
);

// Inventory Accounting (EG-579 to EG-582)
const AccountingReceiptsPage = lazyWithReload(() =>
  import("@/pages/Inventory/accounting-receipts").then((m) => ({
    default: m.AccountingReceiptsPage,
  })),
);
const AccountingIssuesPage = lazyWithReload(() =>
  import("@/pages/Inventory/accounting-issues").then((m) => ({
    default: m.AccountingIssuesPage,
  })),
);
const AccountingTransfersPage = lazyWithReload(() =>
  import("@/pages/Inventory/accounting-transfers").then((m) => ({
    default: m.AccountingTransfersPage,
  })),
);
const ProductsUomPage = lazyWithReload(() =>
  import("@/pages/Inventory/products-uom").then((m) => ({
    default: m.ProductsUOMPage,
  })),
);

// Customers AR (EG-573)
const CustomerARManagementPage = lazyWithReload(
  () => import("@/pages/Customers/ar-management"),
);
const SupplierAPManagementPage = lazyWithReload(
  () => import("@/pages/Suppliers/ap-management"),
);
const CustomerDocumentOffsetPage = lazyWithReload(
  () => import("@/pages/Accounting/DebtSettlements/customer-document-offset"),
);
const CustomerDebtOffsetPage = lazyWithReload(
  () => import("@/pages/Accounting/DebtSettlements/customer-debt-offset"),
);
const RemoveCustomerDocumentOffsetPage = lazyWithReload(
  () =>
    import("@/pages/Accounting/DebtSettlements/remove-customer-document-offset"),
);
const CustomerDocumentOffsetMultiPage = lazyWithReload(
  () =>
    import("@/pages/Accounting/DebtSettlements/customer-document-offset-multi"),
);

function S({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spin />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function EcomFulfillmentRoute() {
  const { stage = "validate" } = useParams();
  const title = `Fulfillment ECom: ${stage}`;
  return <EcomFulfillmentPage stage={stage} title={title} />;
}

/**
 * Chuyển đường dẫn CŨ của Quy trình / Công việc sang E-Office, GIỮ NGUYÊN tham số.
 *
 * Không dùng `<Navigate>` trực tiếp được vì phải bê `:id` / `:projectId` /
 * `:workflowId` sang đường mới — chuyển thẳng về trang danh sách là người dùng
 * mất đúng bản ghi họ đang mở.
 */
function ChuyenHuongEOffice({
  kieu,
}: {
  kieu: "workflowDetail" | "workflowBoard" | "weworkDetail";
}) {
  const { id, projectId, workflowId } = useParams<{
    id?: string;
    projectId?: string;
    workflowId?: string;
  }>();
  const { search } = useLocation();
  let dich: string = ROUTES.eOffice.workflows;
  if (kieu === "workflowDetail" && id) dich = ROUTES.eOffice.workflowDetail(id);
  else if (kieu === "workflowBoard" && workflowId)
    dich = ROUTES.eOffice.workflowBoard(workflowId);
  else if (kieu === "weworkDetail")
    dich = projectId
      ? ROUTES.eOffice.weworkDetail(projectId)
      : ROUTES.eOffice.weworks;
  return <Navigate to={`${dich}${search}`} replace />;
}

function FinanceEodRedirect() {
  const { date } = useParams<{ date?: string }>();
  const search = date ? `?date=${encodeURIComponent(date)}` : "";
  return <Navigate to={`${ROUTES.reports.eod}${search}`} replace />;
}

/**
 * TCR-1192 — chỉ lấy nhánh `auth`, giống hệt cách `RouteErrorBoundary.tsx`
 * tự đọc redux nội bộ (component đó không expose context ra ngoài qua
 * `onError`, chỉ có `(error, info)` — xem file đó để tránh lệch field name).
 */
interface AuthStateSlice {
  auth?: { user?: AuthUser | null };
}

export function App() {
  const { bootstrapping } = useAuthBootstrap();
  useAuthContextSocket();
  const authUser = useSelector(
    (state: AuthStateSlice) => state.auth?.user ?? null,
  );

  const handleRouteError = (error: Error, info: ErrorInfo): void => {
    reportClientError(error, info, {
      tenant_id: authUser?.tenantId,
      user_id: authUser?.id,
    });
  };

  if (bootstrapping) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f0f2f5",
        }}
      >
        {/* antd 5's `tip` only renders when `Spin` wraps children (nest
                    pattern) or has `fullscreen` — a bare `<Spin tip=.../>` with
                    neither silently drops the text and logs a dev warning.
                    Rendering the label as a plain sibling avoids both. */}
        <Spin size="large" />
        <div style={{ marginTop: 12, color: "rgba(0, 0, 0, 0.45)" }}>
          Đang khởi tạo...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* TCR-1189 — tầng ngoài cùng: lưới an toàn cuối. Bắt lỗi render ở
                các trang KHÔNG nằm trong <Layout/> (login, 404, POS fullscreen)
                và cả lỗi của chính <Layout/>. Fallback chiếm trọn viewport vì
                lúc này không còn Sidebar/Header nào để giữ khung. */}
      <RouteErrorBoundary fullScreen onError={handleRouteError}>
        <Routes>
          {/* Public — Auth flow (SITEMAP §1) */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route
            path="/auth/otp"
            element={
              <S>
                <OtpPage />
              </S>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <S>
                <ForgotPasswordPage />
              </S>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <S>
                <ResetPasswordPage />
              </S>
            }
          />
          <Route
            path="/auth/register"
            element={
              <S>
                <RegisterPage />
              </S>
            }
          />
          <Route
            path="/auth/change-password"
            element={
              <S>
                <ChangePasswordPage />
              </S>
            }
          />
          {/* EG-356 Sprint 6 — set password lần đầu sau register OTP (PrivateRoute vì cần token mustSetPassword=true) */}
          <Route
            path="/auth/set-password"
            element={
              <PrivateRoute
                element={
                  <S>
                    <SetPasswordPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/auth/select-store"
            element={
              <PrivateRoute
                element={
                  <S>
                    <SelectStorePage />
                  </S>
                }
              />
            }
          />
          {/* Backward-compat alias: /login → /auth/login */}
          <Route
            path="/login"
            element={<Navigate to={ROUTES.auth.login} replace />}
          />

          {/* POS Customer Display (public, separate screen for customer-facing display) */}
          <Route
            path="/pos/customer-display"
            element={
              <S>
                <PosCustomerDisplayPage />
              </S>
            }
          />

          {/*
           * App người giao hàng (TCR-1521) — ĐỨNG NGOÀI nhánh `/` của Layout ERP.
           *
           * Cố ý không nằm trong Layout chính: người giao cầm điện thoại ngoài
           * đường, thanh bên 40 mục của ERP là thứ cản trở ở kích thước đó.
           * `ShipperLayout` tự lo phần chặn theo vai trò.
           */}
          <Route
            path="/shipper"
            element={
              <PrivateRoute
                element={
                  <S>
                    <ShipperLayout />
                  </S>
                }
              />
            }
          >
            <Route
              index
              element={<Navigate to={ROUTES.shipper.orders} replace />}
            />
            <Route path="orders" element={<ShipperOrdersPage />} />
            <Route path="orders/claim" element={<ShipperClaimOrdersPage />} />
            <Route path="orders/:id" element={<ShipperOrderDetailPage />} />
            <Route path="cod" element={<ShipperCodPage />} />
            <Route
              path="notifications"
              element={<ShipperNotificationsPage />}
            />
            <Route
              path="profile/statistics"
              element={<ShipperDeliveryStatsPage />}
            />
            <Route
              path="profile/cod-statistics"
              element={<ShipperCodStatsPage />}
            />
            <Route
              path="profile/cod-reconciliation"
              element={<ShipperCodReconciliationPage />}
            />
            <Route path="profile" element={<ShipperProfilePage />} />
          </Route>

          {/* Onboarding Wizard — SITEMAP §263-269 (authenticated owner only) */}
          <Route
            path="/onboarding"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/business"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingBusinessPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/store"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingStorePage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/staff"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingStaffPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/products"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingProductsPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/payment"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingPaymentPage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/invoice"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingInvoicePage />
                  </S>
                }
              />
            }
          />
          <Route
            path="/onboarding/complete"
            element={
              <PrivateRoute
                element={
                  <S>
                    <OnboardingCompletePage />
                  </S>
                }
              />
            }
          />

          {/* Phase 2 Setup Wizard — EG-182 (authenticated, optional) */}
          <Route
            path="/setup"
            element={
              <PrivateRoute
                element={
                  <S>
                    <SetupWizardLayout />
                  </S>
                }
              />
            }
          >
            <Route index element={<SetupIndexPage />} />
            <Route path="branding" element={<SetupBrandingStep />} />
            <Route path="catalog" element={<SetupCatalogStep />} />
            <Route path="plugin" element={<SetupPluginStep />} />
            <Route path="hardware" element={<SetupHardwareStep />} />
            <Route path="demo" element={<SetupDemoStep />} />
          </Route>
          <Route
            path="/setup/complete"
            element={
              <PrivateRoute
                element={
                  <S>
                    <SetupCompletePage />
                  </S>
                }
              />
            }
          />

          {/* Public Invoice Lookup (no auth required) */}
          <Route path="/lookup/:code" element={<InvoiceLookupPage />} />

          {/* Public Referral Landing (no auth required) — TCR-1270. Link
                thật chia sẻ ra ngoài cho khách hàng (${origin}/ref/{code}, sinh
                ở src/pages/Referral/index.tsx handleCopyLink) trước đây rơi vào
                catch-all path="*" -> NotFoundPage với nút "Về trang chủ" đẩy
                khách chưa đăng nhập vào /dashboard yêu cầu login nhân viên. */}
          <Route path="/ref/:code" element={<ReferralLandingPage />} />

          {/* ECom OAuth callback result — opened in a separate browser tab. */}
          <Route
            path={ROUTES.ecom.oauthResult}
            element={
              <S>
                <EcomOAuthResultPage />
              </S>
            }
          />

          {/* Mobile-first workspace; reuses the ERP auth and existing feature permissions. */}
          <Route
            path="/miniapp"
            element={
              <PrivateRoute
                element={
                  <S>
                    <MiniappLayout />
                  </S>
                }
                requiredPermissions={[MINIAPP_WAREHOUSE_ACCESS_PERMISSION]}
              />
            }
          >
            <Route
              index
              element={
                <S>
                  <MiniappHomePage />
                </S>
              }
            />
            <Route
              path="scan"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappScanPage />
                    </S>
                  }
                  requiredPermissions={[MINIAPP_STOCK_LOOKUP_PERMISSION]}
                />
              }
            />
            <Route
              path="counts"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappCountsPage />
                    </S>
                  }
                  requiredPermissions={["inventory.counts.manage"]}
                />
              }
            />
            <Route
              path="counts/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <StockCountNewPage />
                    </S>
                  }
                  requiredPermissions={["inventory.counts.manage"]}
                />
              }
            />
            <Route
              path="receiving"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappReceivingPage />
                    </S>
                  }
                  requiredPermissions={[
                    "purchasing.orders.view",
                    "purchasing.orders.manage",
                  ]}
                />
              }
            />
            <Route
              path="receiving/:id/receive"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <PurchasingReceivePage />
                    </S>
                  }
                  requiredPermissions={[
                    "purchasing.orders.view",
                    "purchasing.orders.manage",
                  ]}
                />
              }
            />
            <Route
              path="transfers/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="inventory-transfer"
                        minTier="starter"
                      >
                        <StockTransferDetailPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["inventory.transfers.manage"]}
                />
              }
            />
            <Route
              path="allocations/scan"
              element={
                <PrivateRoute
                  element={<S><MiniappAllocationVoucherPage /></S>}
                  requiredPermissions={["inventory.location.view"]}
                />
              }
            />
            <Route
              path="allocations/:id"
              element={
                <PrivateRoute
                  element={<S><MiniappAllocationVoucherPage /></S>}
                  requiredPermissions={["inventory.location.view"]}
                />
              }
            />
            <Route
              path="locations"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <WarehouseLocationsPage />
                    </S>
                  }
                  requiredPermissions={["inventory.location.view"]}
                />
              }
            />
            <Route
              path="fulfillment/init-logistic"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <EcomInitLogisticPage />
                    </S>
                  }
                  requiredPermissions={["fulfillment.init_logistic.view"]}
                />
              }
            />
            <Route
              path="fulfillment/order-packing"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <EcomOrderPackingPage />
                    </S>
                  }
                  requiredPermissions={["fulfillment.packing.view"]}
                />
              }
            />
            <Route
              path="fulfillment/order-sending"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <EcomOrderSendingPage />
                    </S>
                  }
                  requiredPermissions={["fulfillment.sending.view"]}
                />
              }
            />
            <Route
              path="fulfillment/outbound/wave"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappWavePickingPage />
                    </S>
                  }
                  requiredPermissions={["fulfillment.wave.operate", "fulfillment.wave.manage"]}
                />
              }
            />
            <Route
              path="fulfillment/outbound/wave/:waveId"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappWavePickingPage />
                    </S>
                  }
                  requiredPermissions={["fulfillment.wave.operate", "fulfillment.wave.manage"]}
                />
              }
            />
            <Route
              path="fulfillment/outbound"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappOutboundPage />
                    </S>
                  }
                  requiredPermissions={["inventory.issues.manage", "fulfillment.wave.operate", "fulfillment.wave.manage"]}
                />
              }
            />
            <Route
              path="fulfillment/outbound/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MiniappOutboundPage />
                    </S>
                  }
                  requiredPermissions={["inventory.issues.manage"]}
                />
              }
            />
            <Route
              path="counts/:id/enter"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <StockCountEnterPage />
                    </S>
                  }
                  requiredPermissions={["inventory.counts.manage"]}
                />
              }
            />
            <Route
              path="counts/:id/variance"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <StockCountVariancePage />
                    </S>
                  }
                  requiredPermissions={["inventory.counts.manage"]}
                />
              }
            />
          </Route>

          {/* Protected */}
          <Route path="/" element={<PrivateRoute element={<Layout />} />}>
            <Route index element={<Navigate to={ROUTES.dashboard} replace />} />

            {/* Dashboard — SITEMAP §12-16 */}
            {/* Người giao hàng vào trang chủ thì về app của họ — xem ShipperHomeRedirect. */}
            <Route
              path="dashboard"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <ShipperHomeRedirect>
                        <DashboardPage />
                      </ShipperHomeRedirect>
                    </S>
                  }
                />
              }
            />

            {/* Notifications — SITEMAP §17 */}
            <Route
              path="notifications"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <NotificationsPage />
                    </S>
                  }
                />
              }
            />

            {/* POS — SITEMAP §18. StoreGuard: cashier phải có sid (chọn store) trước khi
            vào POS — owner/manager/inventory/viewer không bị guard chặn (per ADR-011). */}
            <Route
              path="pos"
              element={
                <PrivateRoute
                  element={<StoreGuard element={<POSPage />} />}
                  requiredPermissions={["pos.use"]}
                />
              }
            />

            {/* Orders aggregate — SITEMAP §6 EG-78 (POS+Online+Livestream+Dealer+Phone) */}
            <Route path="orders">
              <Route
                index
                element={
                  <PrivateRoute
                    element={<OrdersPage />}
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path="fulfillment-wave-packing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomBatchPackingWavePage />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path="imports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <OrderImportsPage />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <OrderDetailPage />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <OrderEditPage />
                      </S>
                    }
                    requiredPermissions={["orders.update"]}
                  />
                }
              />
            </Route>

            <Route path="ecom">
              <Route index element={<Navigate to="orders" replace />} />
              {/* TCR-1601 (2026-09-05) — 'orders.view' (dot key POS orders, dùng chung với
                            accountant/inventory/nv_kho...) tách theo đúng permission BE thật đang yêu
                            cầu: 'ecom.orders.view' (orders/cancel-orders, đã có từ Task B1),
                            'ecom.reconciliation.*' (returns/reconciliation, key MỚI — tách đôi ở TCR-1723),
                            'ecom.reports.view' (6 route báo cáo, key MỚI). */}
              <Route
                path="orders"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrdersPage />
                      </S>
                    }
                    requiredPermissions={["ecom.orders.view"]}
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomReportsOverviewPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="performance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomPerformancePage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="products-report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomProductsReportPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="sales-stats"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomSalesStatsPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="staff-stats"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomStaffStatsPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="revenue-report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomRevenueReportPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="expense-report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomExpenseReportPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="return-cancel-tracking"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomReturnCancelTrackingPage />
                      </S>
                    }
                    requiredPermissions={[
                      "ecom.reports.view",
                      "ecom.return_cancel_tracking.view",
                      "ecom.return_cancel_tracking.warehouse.manage",
                      "ecom.return_cancel_tracking.accounting.manage",
                    ]}
                  />
                }
              />
              {/* TCR-2094, Task 7 — gate 'ecom.reports.view', đúng key BE thật yêu cầu ở
                            `EcomDailyChannelReportController` (xem controller trong tcr-erp-be-tenant),
                            KHÔNG phải key mới — nhất quán với mọi route báo cáo Ecom khác ở trên. */}
              <Route
                path="tiktok-daily-report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomTiktokDailyReportPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              {/* TCR-2095, Task 8 — cùng gate 'ecom.reports.view' như trang TikTok ở trên. */}
              <Route
                path="shopee-daily-report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomShopeeDailyReportPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reports.view"]}
                  />
                }
              />
              <Route
                path="cancel-orders"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CancelOrdersPage />
                      </S>
                    }
                    requiredPermissions={["ecom.orders.view"]}
                  />
                }
              />
              {/* TCR-1723 (2026-09-10) — 2 route này tách theo đúng 2 nhánh key mới:
                            đơn hoàn (thủ kho cần, để nhận hàng về nhập kho) và đối soát thanh
                            toán (dữ liệu dòng tiền). Trước đây gate chung 1 key nên không cấp
                            riêng được. */}
              <Route
                path="returns"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomReturnsPage />
                      </S>
                    }
                    requiredPermissions={[
                      "ecom.reconciliation.returned_orders.view",
                    ]}
                  />
                }
              />
              <Route
                path="reconciliation"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomReconciliationPage />
                      </S>
                    }
                    requiredPermissions={["ecom.reconciliation.payouts.view"]}
                  />
                }
              />
              <Route
                path="orders/exceptions"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrderExceptionsPage />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              {/* TCR-1698 #4 — QA báo "Tạo đơn hàng" trùng "Tạo đơn tại quầy (POS)" nên `orders.create`
                            đã bị ẩn khỏi role-editor. Route nhận thêm `pos.orders.create` (PrivateRoute dùng
                            hasAny → OR) để role tạo mới vẫn vào được màn tạo đơn. */}
              <Route
                path="orders/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrderNewPage />
                      </S>
                    }
                    requiredPermissions={["orders.create", "pos.orders.create"]}
                  />
                }
              />
              <Route
                path="orders/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrderDetailPage />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              {/* TCR-1593 (2026-09-05) — BE (EcomSettingsController) đòi đúng 'settings.manage' HOẶC
                            'ecom.settings.manage' (key mới, cấp riêng cho nv_sale_ecom/ecom_sales_manager) — trước
                            đây route dùng 'orders.view' làm OR-gate thay thế, cho phép vào TRANG nhưng mọi API con
                            (warehouse-rules/settlement-mapping/worker-config) vẫn strict 'settings.manage' nên
                            403 ngay khi trang gọi API — đổi đúng theo cặp key BE thật check. */}
              <Route
                path="channels"
                element={
                  <PrivateRoute
                    element={
                      <Navigate to="/ecom/settings?tab=channels" replace />
                    }
                    requiredPermissions={[
                      "settings.manage",
                      "ecom.settings.manage",
                    ]}
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomSettingsPage />
                      </S>
                    }
                    requiredPermissions={[
                      "settings.manage",
                      "ecom.settings.manage",
                    ]}
                  />
                }
              />
              <Route
                path="products"
                element={
                  <PrivateRoute
                    element={
                      <Navigate to="/ecom/settings?tab=product-links" replace />
                    }
                    requiredPermissions={[
                      "settings.manage",
                      "ecom.settings.manage",
                    ]}
                  />
                }
              />
              {/* TCR-2094/2095 — gate đúng permission key BE thật yêu cầu
                            ('ecom.business_brands.manage', `BusinessBrandController`), KHÔNG dùng
                            'settings.manage'/'ecom.settings.manage' của trang Cài đặt chung —
                            tránh lọt FE route nhưng API con trả 403 (bài học TCR-1601/1723). */}
              <Route
                path="settings/business-brands"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomBusinessBrandsPage />
                      </S>
                    }
                    requiredPermissions={["ecom.business_brands.manage"]}
                  />
                }
              />
              <Route
                path="buyers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomDerivedPage type="buyers" title="Người mua ECom" />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path="finance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomDerivedPage
                          type="finance"
                          title="Tài chính ECom"
                        />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              <Route
                path="shipping"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomDerivedPage
                          type="shipping"
                          title="Vận chuyển ECom"
                        />
                      </S>
                    }
                    requiredPermissions={["orders.view"]}
                  />
                }
              />
              {/* TCR-1689 — cùng lớp bug F2.1/F3 đã sửa cho "Gửi hàng"/"Đóng hàng", còn sót đúng route
                            "Chuẩn bị hàng": route đòi 'orders.view' trong khi API thật của trang (GET
                            /ecom/fulfillment/init-logistic/orders, oc-fulfillment.controller.ts) đòi
                            'fulfillment.init_logistic.view'. Role kho/vận hành có key fulfillment nhưng không có
                            'orders.view' bị 403 ngay ở route, không vào nổi trang dù đã vá các API tham chiếu. */}
              <Route
                path="fulfillment/init-logistic"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomInitLogisticPage />
                      </S>
                    }
                    requiredPermissions={["fulfillment.init_logistic.view"]}
                  />
                }
              />
              {/* RBAC fix (2026-09-02, Task F3) — cùng lớp bug F2.1 đã sửa cho "Gửi hàng": route "Đóng
                            hàng" trước đây đòi 'orders.view' trong khi API thật đằng sau (GET
                            /ecom/fulfillment/order-packing/orders, oc-fulfillment.controller.ts) đòi đúng
                            'fulfillment.packing.view'. Task B2.2 (BE, đã merge) vừa cấp đúng key này cho
                            ecom_sales_manager/nv_sale_ecom theo Excel ("Đóng hàng" = Đọc) — đổi route sang đúng
                            key BE thật check. */}
              <Route
                path="fulfillment/order-packing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrderPackingPage />
                      </S>
                    }
                    requiredPermissions={["fulfillment.packing.view"]}
                  />
                }
              />
              {/* RBAC fix (2026-09-02, Task F2.1) — shipper có 'fulfillment.sending.view' nhưng KHÔNG có
                            'orders.view' (Excel: scope shipper CHỈ đúng "Gửi hàng"+"Giao vận", xem role-permissions.service.ts
                            khối `shipper`) — route trước đây đòi 'orders.view' nên shipper luôn bị 403 dù menu đã cho vào
                            (xem menuItems.tsx cùng batch). Đổi đúng theo key route/API thật của trang này. */}
              <Route
                path="fulfillment/order-sending"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomOrderSendingPage />
                      </S>
                    }
                    requiredPermissions={["fulfillment.sending.view"]}
                  />
                }
              />
              {/* TCR-1593 — cùng lý do đã ghi ở route "settings" phía trên: BE (EcomSettingsController,
                            warehouse-rules/worker-config) strict 'settings.manage', 'orders.view' chỉ mở được TRANG
                            chứ không mở API con. */}
              <Route
                path="fulfillment/settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomFulfillmentSettingsPage />
                      </S>
                    }
                    requiredPermissions={[
                      "settings.manage",
                      "ecom.settings.manage",
                    ]}
                  />
                }
              />
              {/* TCR-1807 (2026-09-13) — 5 route dưới đây trước đây đều đòi 'orders.view' dựa theo
                            comment lỗi thời nói BE gác bằng RolesGuard cứng (đã migrate hẳn sang
                            PermissionsGuard + key fulfillment.* từ TCR-1589, 2026-09-05 — re-verify trực tiếp
                            oc-fulfillment.controller.ts/ecom-fulfillment-wave.controller.ts, không còn route
                            nào dùng @Roles literal nữa). Role tuỳ biến chỉ có fulfillment.* (không có
                            'orders.view') bị chặn ở route FE trước khi chạm API. Đổi đúng theo key BE check
                            thật cho TỪNG route — KHÔNG gán cả 3 key fulfillment.{init_logistic,packing,sending}.view
                            như nhau cho mọi route, vì chỉ có đúng 1 route (':stage') thật sự OR cả 3 ở BE:
                            - 'fulfillment/:stage' (EcomFulfillmentRoute, gọi GET /ecom/fulfillment/:stage) —
                              BE tự OR cả 3 key ('fulfillment.init_logistic.view'/'.packing.view'/'.sending.view',
                              oc-fulfillment.controller.ts) vì :stage là param động map sang nhiều giai đoạn khác
                              nhau (FulfillmentService.mapStage) — route FE giữ đúng OR 3 key này.
                            - 'fulfillment/:id/packing' (EcomPackingPage, GET /ecom/fulfillment/:id/packing) —
                              BE CHỈ đòi đúng 1 key 'fulfillment.packing.view' (oc-fulfillment.controller.ts),
                              KHÔNG OR với 2 key kia.
                            - 'fulfillment/:id/handover' (EcomHandoverPage, GET /ecom/fulfillment/:id/handover) —
                              BE CHỈ đòi đúng 1 key 'fulfillment.sending.view' (oc-fulfillment.controller.ts).
                            - 'fulfillment-waves'/'fulfillment-wave-packing' (EcomWavesPage/EcomBatchPackingWavePage)
                              — BE (ecom-fulfillment-wave.controller.ts) OR cả 3 key
                              'fulfillment.wave.view'/'.operate'/'.manage' cho route xem danh sách/trạng thái wave
                              (GET waves, GET waves/:id); các action ghi (scan/skip/cancel/...) đòi .operate
                              hoặc .manage riêng — gate FE ở mức route dùng đúng OR cả 3 để không chặn nhầm ai
                              chỉ có .view (xem được wave nhưng chưa có quyền vận hành), gate nút mutate cụ thể
                              nằm ở BatchPackingWavePage.tsx (canSuperviseWave). */}
              <Route
                path="fulfillment/:stage"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomFulfillmentRoute />
                      </S>
                    }
                    requiredPermissions={[
                      "fulfillment.init_logistic.view",
                      "fulfillment.packing.view",
                      "fulfillment.sending.view",
                    ]}
                  />
                }
              />
              <Route
                path="fulfillment/:id/packing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomPackingPage />
                      </S>
                    }
                    requiredPermissions={["fulfillment.packing.view"]}
                  />
                }
              />
              <Route
                path="fulfillment/:id/handover"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomHandoverPage />
                      </S>
                    }
                    requiredPermissions={["fulfillment.sending.view"]}
                  />
                }
              />
              <Route
                path="fulfillment-waves"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomWavesPage />
                      </S>
                    }
                    requiredPermissions={[
                      "fulfillment.wave.view",
                      "fulfillment.wave.operate",
                      "fulfillment.wave.manage",
                    ]}
                  />
                }
              />
              <Route
                path="fulfillment-wave-packing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <EcomBatchPackingWavePage />
                      </S>
                    }
                    requiredPermissions={[
                      "fulfillment.wave.view",
                      "fulfillment.wave.operate",
                      "fulfillment.wave.manage",
                    ]}
                  />
                }
              />
            </Route>

            {/* Omnichat (P1) — /omnichat mở cho cả 3 key hội thoại (PrivateRoute dùng hasAny → OR);
                        trang Kết nối kênh chỉ dành cho người có quyền quản trị kênh. */}
            <Route
              path="omnichat"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatInboxPage />
                    </S>
                  }
                  requiredPermissions={[
                    "omnichat.conversations.view",
                    "omnichat.conversations.view_all",
                    "omnichat.conversations.manage",
                  ]}
                />
              }
            />
            <Route
              path="omnichat/connections"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatConnectionsPage />
                    </S>
                  }
                  requiredPermissions={["omnichat.channels.manage"]}
                />
              }
            />
            <Route
              path="omnichat/care"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatCareWorklistPage />
                    </S>
                  }
                  requiredPermissions={[
                    "omnichat.conversations.view",
                    "omnichat.conversations.manage",
                    "omnichat.care_rules.manage",
                  ]}
                />
              }
            />
            <Route
              path="omnichat/settings/care-rules"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatCareRulesPage />
                    </S>
                  }
                  requiredPermissions={["omnichat.care_rules.manage"]}
                />
              }
            />
            <Route
              path="omnichat/campaigns"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatCampaignListPage />
                    </S>
                  }
                  requiredPermissions={[
                    "omnichat.conversations.view",
                    "omnichat.campaigns.manage",
                  ]}
                />
              }
            />
            <Route
              path="omnichat/campaigns/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatCampaignEditorPage />
                    </S>
                  }
                  requiredPermissions={["omnichat.campaigns.manage"]}
                />
              }
            />
            <Route
              path="omnichat/campaigns/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatCampaignEditorPage />
                    </S>
                  }
                  requiredPermissions={["omnichat.campaigns.manage"]}
                />
              }
            />
            <Route
              path="omnichat/settings"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatSettingsPage />
                    </S>
                  }
                  requiredPermissions={[
                    "omnichat.channels.manage",
                    "omnichat.ticket_categories.manage",
                    "omnichat.canned_responses.manage",
                    "omnichat.care_rules.manage",
                  ]}
                />
              }
            />
            <Route
              path="omnichat/reports/sla"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <OmnichatSlaReportPage />
                    </S>
                  }
                  requiredPermissions={["omnichat.sla.view", "omnichat.sla.manage"]}
                />
              }
            />

            {/* TCR-1766 — 'b2b.access' là cổng vào cả phân hệ B2B: ẩn menu thôi chưa đủ,
                        gõ thẳng URL `/b2b/...` trước đây vẫn vào được vì mỗi route con chỉ gate
                        theo key nghiệp vụ của riêng nó. `requiredPermissions` của PrivateRoute là
                        OR-gate nên KHÔNG diễn tả được "b2b.access VÀ key con" trong cùng một
                        route — lồng route cha (chỉ render <Outlet/>) mới ra đúng ngữ nghĩa AND:
                        cha chặn theo cổng vào, con giữ nguyên gate nghiệp vụ như cũ.
                        Lưu ý: `customers/groups` bên dưới (TCR-1747) từ nay cũng nằm sau cổng này;
                        role ngoài B2B vẫn vào màn đó bằng đường chính `/customers/groups`. */}
            <Route
              path="b2b"
              element={
                <PrivateRoute
                  element={<Outlet />}
                  requiredPermissions={["b2b.access"]}
                />
              }
            >
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BPage />
                      </S>
                    }
                    requiredPermissions={["b2b.access"]}
                  />
                }
              />
              <Route path="pricing">
                <Route
                  index
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <B2BPricingPage />
                        </S>
                      }
                      requiredPermissions={["b2b.pricing.view"]}
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <B2BPricingNewPage />
                        </S>
                      }
                      requiredPermissions={["b2b.pricing.manage"]}
                    />
                  }
                />
                <Route path="overrides">
                  <Route
                    path="new"
                    element={
                      <PrivateRoute
                        element={
                          <S>
                            <B2BPriceOverrideNewPage />
                          </S>
                        }
                        requiredPermissions={["b2b.pricing.view"]}
                      />
                    }
                  />
                  <Route path=":id">
                    <Route
                      index
                      element={
                        <PrivateRoute
                          element={
                            <S>
                              <B2BPriceOverrideDetailPage />
                            </S>
                          }
                          requiredPermissions={["b2b.pricing.view"]}
                        />
                      }
                    />
                    <Route
                      path="edit"
                      element={
                        <PrivateRoute
                          element={
                            <S>
                              <B2BPriceOverrideEditPage />
                            </S>
                          }
                          requiredPermissions={["b2b.pricing.view"]}
                        />
                      }
                    />
                  </Route>
                </Route>
                <Route path=":id">
                  <Route
                    index
                    element={
                      <PrivateRoute
                        element={
                          <S>
                            <B2BPricingDetailPage />
                          </S>
                        }
                        requiredPermissions={["b2b.pricing.view"]}
                      />
                    }
                  />
                  <Route
                    path="edit"
                    element={
                      <PrivateRoute
                        element={
                          <S>
                            <B2BPricingEditPage />
                          </S>
                        }
                        requiredPermissions={["b2b.pricing.manage"]}
                      />
                    }
                  />
                </Route>
              </Route>
              <Route path="rebates">
                <Route
                  index
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <B2BRebatesPage />
                        </S>
                      }
                      requiredPermissions={["b2b.rebates.view"]}
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <B2BRebatesNewPage />
                        </S>
                      }
                      requiredPermissions={["b2b.rebates.view"]}
                    />
                  }
                />
                <Route
                  path="ledger/:customerId"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <B2BRebateCustomerLedgerDetailPage />
                        </S>
                      }
                      requiredPermissions={["b2b.rebates.view"]}
                    />
                  }
                />
                <Route path=":id">
                  <Route
                    index
                    element={
                      <PrivateRoute
                        element={
                          <S>
                            <B2BRebatesDetailPage />
                          </S>
                        }
                        requiredPermissions={["b2b.rebates.view"]}
                      />
                    }
                  />
                  <Route
                    path="edit"
                    element={
                      <PrivateRoute
                        element={
                          <S>
                            <B2BRebatesEditPage />
                          </S>
                        }
                        requiredPermissions={["b2b.rebates.view"]}
                      />
                    }
                  />
                </Route>
              </Route>
              <Route
                path="orders"
                element={<Navigate to={ROUTES.orders.list} replace />}
              />
              <Route
                path="receivables"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BReceivablesPage />
                      </S>
                    }
                    requiredPermissions={["b2b.receivables.view"]}
                  />
                }
              />
              <Route
                path="receivables/customers/:customerId"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BReceivableCustomerDetailPage />
                      </S>
                    }
                    requiredPermissions={["b2b.receivables.view"]}
                  />
                }
              />
              <Route
                path="gift-debts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BGiftDebtsPage />
                      </S>
                    }
                    requiredPermissions={["b2b.gift_debts.view"]}
                  />
                }
              />
              {/* TCR-1522 Task 8 — chỉ ke_toan_truong có permission_keys chứa
                        'b2b.orders.credit_approve' (xem role-permissions.service.ts BE) mới qua
                        được guard này; các role B2B khác (b2b_sales, b2b_sales_manager...) không
                        có key này nên bị PrivateRoute render 403 Forbidden. */}
              <Route
                path="credit-approvals"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BCreditApprovalsPage />
                      </S>
                    }
                    requiredPermissions={["b2b.orders.credit_approve"]}
                  />
                }
              />
              {/* TCR-1747 — `/b2b/customers/groups` dựng ĐÚNG cùng màn "Quản lý Nhóm
                            khách hàng" như `/customers/groups`, nên phải gate cùng một key; để
                            nguyên 'customers.view'/'b2b.*' là chừa sẵn một cửa sau vào đúng màn
                            vừa chặn. */}
              <Route
                path="customers/groups"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerGroupsPage />
                      </S>
                    }
                    requiredPermissions={["customers.groups.view"]}
                  />
                }
              />
              {/* TCR-1768 — BE POST /customers (tạo KH) gate bằng Permissions.CUSTOMERS_CREATE
                        ('customers.create', customer.controller.ts:108), KHÔNG phải customers.manage.
                        customers.manage đã bị ẩn khỏi role-editor (TCR-1762) nên role mới không còn
                        cách nào được cấp — thêm customers.create làm OR-alternative để NVBH B2B có
                        đúng quyền tạo KH vẫn vào được màn này (giữ nguyên customers.manage cho role
                        cũ đã có sẵn quyền đó).
                        TCR-1770 — 'b2b.crm.view' đã bị xoá khỏi hệ thống (quyền legacy dư thừa),
                        bỏ khỏi OR-list ở đây và 4 route B2B CRM ngay dưới. */}
              {/* TCR-1768 (QA phản hồi vòng 2, 2026-09-12) — thêm 'b2b.customers.create' làm
                        OR-alternative thứ 3: BE giờ cấp key B2B-labeled này song song 'customers.create'
                        cho b2b_sales/b2b_sales_manager (route POST /v1/customers chấp nhận cả 2 key),
                        FE cũng phải nhận key mới để role tương lai CHỈ có key B2B vẫn vào được màn này. */}
              <Route
                path="customers/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BCustomerNewPage />
                      </S>
                    }
                    requiredPermissions={[
                      "customers.manage",
                      "customers.create",
                      "b2b.customers.create",
                    ]}
                  />
                }
              />
              {/* BE (b2b-crm.controller.ts) gate GET routes bằng Permissions.B2B_CUSTOMERS_VIEW
                        ('b2b.customers.view') — key duy nhất, đủ cho MỌI role đang truy cập màn này
                        (TCR-1770 xác nhận: mọi role hệ thống từng có 'b2b.crm.view' đều đã có sẵn
                        key này rồi, không mất quyền ai khi bỏ OR-alternative cũ). */}
              <Route
                path="customers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BCrmCustomersPage />
                      </S>
                    }
                    requiredPermissions={["b2b.customers.view"]}
                  />
                }
              />
              <Route
                path="promotions"
                element={<Navigate to={ROUTES.promotions.list} replace />}
              />
              <Route
                path="combo-gifts"
                element={<Navigate to={ROUTES.products.combos} replace />}
              />
              <Route
                path="crm"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BCrmPage />
                      </S>
                    }
                    requiredPermissions={["b2b.customers.view"]}
                  />
                }
              />
              <Route
                path="crm/customers/:customerId"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BCustomer360Page />
                      </S>
                    }
                    requiredPermissions={["b2b.customers.view"]}
                  />
                }
              />
              <Route
                path="commission"
                element={<Navigate to={ROUTES.commission.index} replace />}
              />
              {/* TCR-1831 — 'b2b.reports.view' (quyền gộp) đã tách thành 4 key con theo
                        tab (executive/revenue/staff_kpi/rebate.view — xem B2B_REPORT_TABS ở
                        B2B/Reports/index.tsx) và bị $pull khỏi mọi role qua migration. Gate route
                        bằng OR cả 4 (PrivateRoute dùng hasAny) — vào được trang nếu có ÍT NHẤT 1
                        tab để xem; nội dung Tabs tự lọc đúng tab theo quyền con bên trong. */}
              <Route
                path="executive-dashboard"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BReportsPage />
                      </S>
                    }
                    requiredPermissions={[
                      "b2b.reports.executive.view",
                      "b2b.reports.revenue.view",
                      "b2b.reports.staff_kpi.view",
                      "b2b.reports.rebate.view",
                    ]}
                  />
                }
              />
              {/* TCR-1587 — BE (b2b-staff-inventory.controller.ts) đòi đúng
                        'b2b.staff_inventory.view', không phải 'b2b.access'. Route guard trước đây
                        chỉ chấp nhận 'b2b.access' nên thủ kho (chỉ có key .staff_inventory.view,
                        không có b2b.access) dù menu đã sửa vẫn 403 khi bấm vào. hasAny() bên trong
                        PrivateRoute là OR-gate nên thêm key không nới quyền cho role khác.
                        TCR-1766 — giữ NGUYÊN cặp key này: route cha đã chặn 'b2b.access' nên
                        'b2b.access' ở đây thành dư, nhưng bỏ nó đi là siết thêm cho role có cổng
                        vào mà không có '.staff_inventory.view' (vd accountant) — ngoài phạm vi
                        ticket, và BE vẫn 403 đúng API của màn này. */}
              <Route
                path="staff-inventory"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BStaffInventoryPage />
                      </S>
                    }
                    requiredPermissions={[
                      "b2b.access",
                      "b2b.staff_inventory.view",
                    ]}
                  />
                }
              />
              {/* TCR-1770 — 'b2b.crm.view' đã bị xoá (quyền legacy, không liên quan gì tới
                        Cài đặt B2B — chỉ "tình cờ" gate đúng route này vì mọi role có crm.view cũng
                        có luôn b2b.settings.view). Đổi sang key BE THẬT enforce (b2b-settings
                        .controller.ts: GET/PATCH gate bằng 'b2b.settings.view'/'.manage') — verify
                        mọi role hệ thống từng vào được màn này đều đã có sẵn key này. */}
              <Route
                path="settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <B2BSettingsPage />
                      </S>
                    }
                    requiredPermissions={["b2b.settings.view"]}
                  />
                }
              />
            </Route>

            {/* POS extras — SITEMAP §29-32. StoreGuard guard cashier no-sid (ADR-011). */}
            <Route
              path="pos/returns"
              element={
                <PrivateRoute
                  element={<S><PosReturnsPage /></S>}
                  requiredPermissions={["pos.return_requests.view"]}
                />
              }
            />
            <Route
              path="pos/buy-in"
              element={
                <PrivateRoute
                  element={<Navigate to={ROUTES.buyback.list} replace />}
                  requiredPermissions={["pos.buy_in"]}
                />
              }
            />
            <Route path="buyback">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="buyback" minTier="starter">
                          <BuybackListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.buy_in"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="buyback" minTier="starter">
                          <BuybackNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.buy_in"]}
                  />
                }
              />
              <Route
                path="reports"
                element={<Navigate to={ROUTES.reports.buyback} replace />}
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="buyback" minTier="starter">
                          <BuybackDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.buy_in"]}
                  />
                }
              />
            </Route>
            <Route path="exchanges">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="exchange" minTier="starter">
                          <ExchangeListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.refund"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="exchange" minTier="starter">
                          <ExchangeNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.refund"]}
                  />
                }
              />
              <Route
                path="reports"
                element={<Navigate to={ROUTES.reports.exchange} replace />}
              />
              <Route
                path=":id/approve"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="exchange" minTier="starter">
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <ExchangeApprovePage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.refund"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="exchange" minTier="starter">
                          <ExchangeDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["pos.refund"]}
                  />
                }
              />
            </Route>
            <Route
              path="pos/shifts"
              element={
                <PrivateRoute
                  element={
                    <StoreGuard
                      element={
                        <S>
                          <PosShiftsPage />
                        </S>
                      }
                    />
                  }
                  requiredPermissions={["pos.shifts_manage"]}
                />
              }
            />

            {/* Customers — SITEMAP §162-168 */}
            <Route
              path="hrm"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmDashboardPage />
                    </S>
                  }
                  requiredPermissions={["hrm.dashboard.view"]}
                />
              }
            />
            <Route
              path="hrm/employees/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeeFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.employees.manage"]}
                />
              }
            />
            <Route
              path="hrm/employees/:id/edit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeeFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.employees.manage"]}
                />
              }
            />
            <Route
              path="me"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeeSelfServicePage />
                    </S>
                  }
                  // Hồ sơ CỦA CHÍNH MÌNH: mọi tài khoản đã đăng nhập đều vào được (2026-10-08),
                  // máy chủ cũng đã bỏ quyền ở các route /hrm/employees/me và /hrm/self/*.
                />
              }
            />
            <Route
              path="hrm/employee-change-requests"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeeChangeRequestsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.change_requests.view"]}
                />
              }
            />
            <Route
              path="hrm/employees/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeeDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.employees.view"]}
                />
              }
            />
            <Route
              path="hrm/employees"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmEmployeesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.employees.view"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentRequestFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.manage"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/candidates/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentCandidateFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.manage"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/candidates"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentCandidatesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.view"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/approvals"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentApprovalsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.view"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/:requestId/candidates/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentCandidateFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.manage"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/:requestId/candidates/:candidateId/edit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentCandidateFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.manage"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/:id/edit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentRequestFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.manage"]}
                />
              }
            />
            <Route
              path="hrm/recruitment/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.view"]}
                />
              }
            />
            <Route
              path="hrm/recruitment"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRecruitmentPage />
                    </S>
                  }
                  requiredPermissions={["hrm.recruitment.view"]}
                />
              }
            />
            <Route
              path="hrm/departments"
              // Phòng ban và Bộ phận nay là cấp trong cây "Cơ cấu tổ chức".
              element={<Navigate to={ROUTES.hrm.positions} replace />}
            />
            <Route
              path="hrm/sub-departments"
              element={
                <Navigate to={`${ROUTES.hrm.positions}?view=flat`} replace />
              }
            />
            <Route
              path="hrm/offices"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmOfficesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.offices.view"]}
                />
              }
            />
            <Route
              path="hrm/positions/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmPositionDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.org.view"]}
                />
              }
            />
            <Route
              path="hrm/positions"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmPositionsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.org.view"]}
                />
              }
            />
            <Route
              path="hrm/legal-entities/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmLegalEntityDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.legal_entities.view"]}
                />
              }
            />
            <Route
              path="hrm/legal-entities"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmLegalEntitiesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.legal_entities.view"]}
                />
              }
            />
            <Route
              path="hrm/asset-types"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmAssetTypesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.settings.view"]}
                />
              }
            />
            <Route
              path="hrm/org-chart"
              /*
               * Sơ đồ tổ chức nay là một CHẾ ĐỘ XEM của trang "Cơ cấu tổ chức",
               * không còn là trang riêng. Chuyển hướng thay vì xoá route: link cũ,
               * dấu trang và tài liệu vẫn còn trỏ tới đây.
               */
              element={
                <Navigate to={`${ROUTES.hrm.positions}?view=map`} replace />
              }
            />
            <Route
              path="hrm/basevn"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmBaseVNPage />
                    </S>
                  }
                  requiredPermissions={["hrm.basevn.view"]}
                />
              }
            />
            <Route
              path="hrm/basevn-sync-log"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmBaseVNSyncLogPage />
                    </S>
                  }
                  requiredPermissions={["hrm.basevn.view"]}
                />
              }
            />
            <Route
              path="hrm/settings"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmSettingsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.settings.view"]}
                />
              }
            />
            <Route
              path="hrm/attendance-policy"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmAttendancePolicyPage />
                    </S>
                  }
                  requiredPermissions={["hrm.settings.view"]}
                />
              }
            />
            <Route
              path="hrm/attendance-config"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmAttendanceConfigPage />
                    </S>
                  }
                  requiredPermissions={["hrm.attendance.view"]}
                />
              }
            />
            <Route
              path="hrm/document-types"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmDocumentTypesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.settings.view"]}
                />
              }
            />
            <Route
              path="hrm/work-regions"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWorkRegionsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.settings.view"]}
                />
              }
            />
            <Route
              path="hrm/internal-documents/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDocumentFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.internal_documents.manage"]}
                />
              }
            />
            <Route
              path="hrm/internal-documents/import"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDocumentImportPage />
                    </S>
                  }
                  requiredPermissions={[
                    ...HR_IMPORT_WRITE_PERMISSIONS.internalDocuments,
                  ]}
                />
              }
            />
            <Route
              path="hrm/internal-documents/import-decisions"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDecisionImportPage />
                    </S>
                  }
                  requiredPermissions={[
                    ...HR_IMPORT_WRITE_PERMISSIONS.internalDocuments,
                  ]}
                />
              }
            />
            <Route
              path="hrm/internal-documents/:id/edit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDocumentFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.internal_documents.manage"]}
                />
              }
            />
            <Route
              path="hrm/internal-documents/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDocumentDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.internal_documents.view"]}
                />
              }
            />
            <Route
              path="hrm/internal-documents"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInternalDocumentsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.internal_documents.view"]}
                />
              }
            />
            {/* `new` phải đứng TRƯỚC `:id`, nếu không "new" sẽ bị nuốt làm id. */}
            <Route
              path="hrm/contracts/new"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmContractFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.contracts.manage"]}
                />
              }
            />
            <Route
              path="hrm/contracts/:id/edit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmContractFormPage />
                    </S>
                  }
                  requiredPermissions={["hrm.contracts.manage"]}
                />
              }
            />
            <Route
              path="hrm/contracts/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmContractDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.contracts.view"]}
                />
              }
            />
            <Route
              path="hrm/contracts"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmContractsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.contracts.view"]}
                />
              }
            />
            {/* Route con đặt TRƯỚC `hrm/kpis` để không bị route cha nuốt mất. */}
            <Route
              path="hrm/kpis/import"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmKpiScorecardImportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.kpi.manage"]}
                />
              }
            />
            <Route
              path="hrm/kpis"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmKpiScorecardsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.kpi.view"]}
                />
              }
            />
            <Route
              path="hrm/payrolls"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmPayrollPage />
                    </S>
                  }
                  requiredPermissions={["hrm.payroll.view"]}
                />
              }
            />
            <Route
              path="hrm/benefits"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmBenefitsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.benefits.view"]}
                />
              }
            />
            <Route
              path="hrm/insurance"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmInsuranceRosterPage />
                    </S>
                  }
                  requiredPermissions={["hrm.insurance.view"]}
                />
              }
            />
            <Route
              path="hrm/attendances"
              // Route này trước nay KHÔNG có cổng quyền nào, dù dữ liệu chính của trang
              // (`attendance.controller.ts`) đã gác bằng `hrm.attendance.view` từ lâu —
              // nghĩa là người không có quyền vẫn vào được rồi nhận 403 rải rác. Nay
              // danh sách file tổng hợp công cũng đã đóng lại (2026-09-10), phần duy
              // nhất còn chạy được cũng hết, nên gác luôn ở route để hiện màn 403 tử tế
              // thay vì một trang hỏng từng mảnh.
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmAttendancePage />
                    </S>
                  }
                  requiredPermissions={["hrm.attendance.view"]}
                />
              }
            />
            <Route
              path="e-office/workflows/board/:workflowId"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWorkflowBoardPage />
                    </S>
                  }
                  requiredPermissions={["hrm.workflows.view"]}
                />
              }
            />
            <Route
              path="e-office/workflows/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWorkflowJobDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.workflows.view"]}
                />
              }
            />
            <Route
              path="e-office/workflows"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWorkflowsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.workflows.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/workflows"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWorkflowsReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.workflows.view"]}
                />
              }
            />
            <Route
              path="e-office/weworks"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWeworksPage />
                    </S>
                  }
                  requiredPermissions={["hrm.weworks.view"]}
                />
              }
            />
            <Route
              path="e-office/weworks/:projectId"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWeworkProjectDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.weworks.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/weworks"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmWeworksReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.weworks.view"]}
                />
              }
            />
            {/*
             * Đường dẫn CŨ của Quy trình / Công việc, giữ lại để chuyển hướng.
             *
             * Hai màn này đã dọn sang E-Office. Link đã gửi qua chat, dấu trang
             * của người dùng và thông báo cũ vẫn trỏ về `/hrm/...`; bỏ hẳn thì
             * những đường đó rơi vào trang 404 mà không ai biết vì sao.
             * `replace` để nút Back không kẹt vòng giữa hai đường dẫn.
             */}
            <Route
              path="hrm/workflows/board/:workflowId"
              element={<ChuyenHuongEOffice kieu="workflowBoard" />}
            />
            <Route
              path="hrm/workflows/:id"
              element={<ChuyenHuongEOffice kieu="workflowDetail" />}
            />
            <Route
              path="hrm/workflows"
              element={<Navigate to={ROUTES.eOffice.workflows} replace />}
            />
            <Route
              path="hrm/weworks/:projectId"
              element={<ChuyenHuongEOffice kieu="weworkDetail" />}
            />
            <Route
              path="hrm/weworks"
              element={<Navigate to={ROUTES.eOffice.weworks} replace />}
            />
            <Route
              path="hrm/requests"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRequestsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.requests.view"]}
                />
              }
            />
            <Route
              path="hrm/requests/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRequestDetailPage />
                    </S>
                  }
                  requiredPermissions={["hrm.requests.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/requests"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmRequestsReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.requests.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/schedules"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmSchedulesReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.schedules.view"]}
                />
              }
            />
            <Route
              path="hrm/schedules"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmSchedulesPage />
                    </S>
                  }
                  requiredPermissions={["hrm.schedules.view"]}
                />
              }
            />
            <Route
              path="hrm/overtime"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmOvertimePage />
                    </S>
                  }
                  requiredPermissions={["hrm.overtime.view"]}
                />
              }
            />
            <Route
              path="hrm/timeoffs"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmTimeoffsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.timeoff.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/timeoffs"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmTimeoffsReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.timeoff.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/annual-leave"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmAnnualLeaveReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.reports.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/turnover"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmTurnoverReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.reports.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/trend"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmTrendReportPage />
                    </S>
                  }
                  requiredPermissions={["hrm.reports.view"]}
                />
              }
            />
            <Route
              path="hrm/reports/policy-documents"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <HrmPolicyReportDocumentsPage />
                    </S>
                  }
                  requiredPermissions={["hrm.reports.view"]}
                />
              }
            />

            {/* Customers — SITEMAP §162-168 */}
            <Route path="customers">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomersPage />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              {/* TCR-1919 — màn TẠO khách hàng: `POST /v1/customers` đòi
                            'customers.create'/'b2b.customers.create' (TCR-1726/TCR-1768), còn key
                            gộp 'customers.manage' đã nghỉ hưu và bị gỡ khỏi mọi vai trò lúc app
                            boot. Nếu để gate cũ manage-only thì sau khi dọn, chỉ owner/Quản lý
                            (wildcard) vào được màn này. Giữ 'customers.manage' trong danh sách
                            (OR-gate) cho vai trò chưa kịp dọn — khớp đúng route B2B tương ứng. */}
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerNewPage />
                      </S>
                    }
                    requiredPermissions={[
                      "customers.manage",
                      "customers.create",
                      "b2b.customers.create",
                    ]}
                  />
                }
              />
              {/* TCR-1747 — trước fix gate bằng 'customers.manage', nên NVBH B2B (có
                            customers.manage để sửa khách được giao) vào thẳng được màn Nhóm khách
                            hàng và tạo nhóm. Nay đòi ĐÚNG key riêng của nhóm KH. */}
              <Route
                path="groups"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerGroupsPage />
                      </S>
                    }
                    requiredPermissions={["customers.groups.view"]}
                  />
                }
              />

              <Route
                path="personal"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomersPage customerType={UserType.PERSONAL} />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              <Route
                path="organization"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomersPage customerType={UserType.ORGANIZATION} />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              {/* TCR-1919 — màn SỬA khách hàng phải nhận 'customers.update' (key tách
                            từ TCR-1758/1762 mà `PUT /v1/customers/:id` vốn đã chấp nhận): key gộp
                            'customers.manage' đã nghỉ hưu, để gate cũ là vai trò tick "Sửa khách
                            hàng" vẫn bị chặn ở cổng route. */}
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerEditPage />
                      </S>
                    }
                    /* TCR-1952 — nhận thêm 'customers.invoice_email.update': form
                                       sửa khách là nơi DUY NHẤT có ô "Email xuất HĐ", chặn ở cổng
                                       route là quyền mới không dùng được vào việc gì. Các ô còn
                                       lại của form tự khoá theo quyền (CustomerModal).
                       TCR-2511 — 'customers.phone.update' cùng lý do (ô "Số điện thoại"). */
                    requiredPermissions={[
                      "customers.manage",
                      "customers.update",
                      "customers.invoice_email.update",
                      "customers.phone.update",
                    ]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerDetailPage />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              <Route
                path=":id/purchases"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerPurchasesPage />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              <Route
                path=":id/loyalty"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerLoyaltyPage />
                      </S>
                    }
                    requiredPermissions={["customers.view"]}
                  />
                }
              />
              {/* TCR-1759 — route trước đây chỉ đòi 'customers.view', nên gõ thẳng URL vẫn
                        vào được trang Số dư dù không có quyền xem tiền ký quỹ. Đổi đúng key route
                        thật (customer-credit.controller.ts) yêu cầu. */}
              <Route
                path=":id/credits"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CustomerCreditsPage />
                      </S>
                    }
                    requiredPermissions={["customers.credit.view"]}
                  />
                }
              />
            </Route>

            {/* Products — SITEMAP §174-176 */}
            <Route path="products">
              <Route
                index
                element={
                  <PrivateRoute
                    element={<ProductsPage />}
                    requiredPermissions={["products.view"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ProductNewPage />
                      </S>
                    }
                    requiredPermissions={["products.create"]}
                  />
                }
              />
              {/* Categories */}
              <Route
                path="categories"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CategoriesPage />
                      </S>
                    }
                    requiredPermissions={CATEGORY_GATE}
                  />
                }
              />

              {/* Metal Categories */}
              <Route
                path="metal-categories"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <MetalCategoriesPage />
                      </S>
                    }
                    requiredPermissions={CATEGORY_GATE}
                  />
                }
              />

              {/* Brands */}
              <Route
                path="brands"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <BrandsPage />
                      </S>
                    }
                    requiredPermissions={BRAND_GATE}
                  />
                }
              />

              {/* Attributes */}
              <Route
                path="attributes"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <AttributesPage />
                      </S>
                    }
                    requiredPermissions={ATTRIBUTE_GATE}
                  />
                }
              />
              <Route
                path="units"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ProductUnitsPage />
                      </S>
                    }
                    requiredPermissions={UNIT_GATE}
                  />
                }
              />
              <Route path="combos-fix">
                <Route
                  index
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductCombosFixPage />
                        </S>
                      }
                      requiredPermissions={["combos.view"]}
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductComboFixFormPage />
                        </S>
                      }
                      requiredPermissions={["combos.manage"]}
                    />
                  }
                />
                <Route
                  path=":id"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductComboFixFormPage />
                        </S>
                      }
                      requiredPermissions={["combos.manage"]}
                    />
                  }
                />
                <Route
                  path=":id/edit"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductComboFixFormPage />
                        </S>
                      }
                      requiredPermissions={["combos.manage"]}
                    />
                  }
                />
              </Route>
              <Route path="combos">
                <Route
                  index
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductCombosPage />
                        </S>
                      }
                      requiredPermissions={["combos.view"]}
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductComboNewPage />
                        </S>
                      }
                      requiredPermissions={["combos.manage"]}
                    />
                  }
                />
                <Route
                  path=":id/edit"
                  element={
                    <PrivateRoute
                      element={
                        <S>
                          <ProductComboNewPage />
                        </S>
                      }
                      requiredPermissions={["combos.manage"]}
                    />
                  }
                />
              </Route>
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ProductDetailPage />
                      </S>
                    }
                    requiredPermissions={["products.view"]}
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ProductEditPage />
                      </S>
                    }
                    requiredPermissions={["products.update"]}
                  />
                }
              />
            </Route>

            {/* Services — catalog dịch vụ (mirror Products) */}
            <Route path="services">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ServicesPage />
                      </S>
                    }
                    requiredPermissions={["services.view"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ServiceNewPage />
                      </S>
                    }
                    requiredPermissions={["services.create"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ServiceDetailPage />
                      </S>
                    }
                    requiredPermissions={["services.view"]}
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ServiceEditPage />
                      </S>
                    }
                    requiredPermissions={["services.update"]}
                  />
                }
              />
            </Route>

            {/* Inventory — SITEMAP §93-110 */}
            {/* RBAC fix TCR (2026-09-02, Task F1) — toàn bộ requiredPermissions dưới nhánh
                        /inventory đã đổi từ 5 key legacy pre-redesign
                        (inventory.view/stock_in/stock_out/transfer/adjust) sang key hiện hành
                        (catalogue thật: role-seed.service.ts). nv_kho (và role tương lai không có
                        5 key legacy) trước fix bị 403 CỨNG trên MỌI route ở đây dù BE đã cấp đúng
                        bộ key mới — xem task-F1-report.md để có bảng mapping đầy đủ + lý do chọn
                        key cho từng route (đặc biệt costing/warehouses dùng key riêng hẹp hơn
                        'inventory.stock.view' để KHÔNG nới quyền ngoài Excel cho nv_kho/
                        nv_mua_hang). owner/manager/inventory (Thủ kho) không bị ảnh hưởng — pass
                        mọi literal key nhờ wildcard 'inventory.*'/'*'. */}
            <Route path="inventory">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.stock.view"]}
                  />
                }
              />
              {/* Receipts — §94-96 */}
              <Route
                path="receipts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsReceiptListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.receipts.manage"]}
                  />
                }
              />
              <Route
                path="receipts/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsReceiptNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.receipts.manage"]}
                  />
                }
              />
              <Route
                path="receipts/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsReceiptDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.receipts.manage"]}
                  />
                }
              />
              <Route
                path="receipts/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsReceiptEditPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.receipts.manage"]}
                  />
                }
              />
              {/* Issues — §97-99 */}
              <Route
                path="issues"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsIssueListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.issues.manage"]}
                  />
                }
              />
              <Route
                path="issues/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsIssueNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.issues.manage"]}
                  />
                }
              />
              <Route
                path="issues/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsIssueDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.issues.manage"]}
                  />
                }
              />
              <Route
                path="issues/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <GoodsIssueEditPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.issues.manage"]}
                  />
                }
              />
              {/* Assembly orders — TCR-1517 Task 7, Lệnh lắp ráp / tháo dỡ combo thật */}
              <Route
                path="assembly-orders"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <AssemblyOrdersPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.assembly.view"]}
                  />
                }
              />
              {/* Transfers — §100-102 [S] */}
              <Route
                path="transfers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="inventory-transfer"
                          minTier="starter"
                        >
                          <StockTransferListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.transfers.manage"]}
                  />
                }
              />
              <Route
                path="transfers/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="inventory-transfer"
                          minTier="starter"
                        >
                          <StockTransferNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.transfers.manage"]}
                  />
                }
              />
              <Route
                path="transfers/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="inventory-transfer"
                          minTier="starter"
                        >
                          <StockTransferDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.transfers.manage"]}
                  />
                }
              />
              <Route
                path="transfers/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="inventory-transfer"
                          minTier="starter"
                        >
                          <StockTransferEditPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.transfers.manage"]}
                  />
                }
              />
              <Route
                path="transfers/:id/approve"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="inventory-transfer"
                          minTier="starter"
                        >
                          <StockTransferDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.transfers.manage"]}
                  />
                }
              />
              {/* Counts — §103-106 */}
              <Route
                path="counts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockCountListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.counts.manage"]}
                  />
                }
              />
              <Route
                path="counts/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockCountNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.counts.manage"]}
                  />
                }
              />
              <Route
                path="counts/:id/enter"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockCountEnterPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.counts.manage"]}
                  />
                }
              />
              <Route
                path="counts/:id/variance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockCountVariancePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.counts.manage"]}
                  />
                }
              />
              {/* Movements / Alerts / Reports / Print — §107-110 */}
              <Route
                path="movements"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryMovementsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.movements.view"]}
                  />
                }
              />
              <Route
                path="alerts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryAlertsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.reorder_alert.view"]}
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryReportsPage4d />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.reports.view"]}
                  />
                }
              />
              <Route
                path="costing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryCostingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.costing.view"]}
                  />
                }
              />
              <Route
                path="cost-exceptions"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryCostExceptionsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.costing.view"]}
                  />
                }
              />
              <Route
                path="print-labels"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryPrintLabelsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.stock.view"]}
                  />
                }
              />
              {/* Warehouses — quản lý kho theo chi nhánh */}
              <Route
                path="warehouses/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <WarehouseNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.warehouse.manage"]}
                  />
                }
              />
              <Route
                path="warehouses"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <WarehousesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.warehouse.view"]}
                  />
                }
              />
              <Route
                path="warehouse-locations"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <WarehouseLocationsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.location.view"]}
                  />
                }
              />
              <Route
                path="lots"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <InventoryLotsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.lot.view"]}
                  />
                }
              />
              <Route
                path="recalls"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockRecallsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.lot.view"]}
                  />
                }
              />
              <Route
                path="recalls/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockRecallNewPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.lot.manage"]}
                  />
                }
              />
              <Route
                path="recalls/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="inventory" minTier="lite">
                          <StockRecallDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["inventory.lot.view"]}
                  />
                }
              />
            </Route>

            {/* Staff — SITEMAP §125-130 */}
            <Route path="staff">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="lite">
                          <StaffsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.view"]}
                  />
                }
              />
              <Route
                path="invite"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <StaffInvitePage />
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.manage"]}
                  />
                }
              />
              <Route
                path=":id/activity"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="lite">
                          <StaffActivityPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.view"]}
                  />
                }
              />
              <Route
                path=":id/stores"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="professional">
                          <StaffStoresPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.manage"]}
                  />
                }
              />
              <Route
                path=":id/permissions"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="lite">
                          <StaffDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.manage"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="lite">
                          <StaffDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.view"]}
                  />
                }
              />
              {/* EG-510 — Payroll Profile tab route (renders StaffDetailPage with payroll tab active) */}
              <Route
                path=":id/payroll-profile"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="staff" minTier="lite">
                          <StaffDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["hrm.accounts.manage"]}
                  />
                }
              />
            </Route>

            {/* TCR-2542 — Trung tâm báo cáo. Gate ngoài là hợp OR mọi permission key
            trong registry (REPORT_CENTER_ANY_PERMISSIONS) — ai thấy được ÍT NHẤT 1
            báo cáo thì thấy trung tâm. Route chi tiết gác quyền RIÊNG từng báo cáo
            bên trong ReportOutlet (registry là nguồn sự thật, thêm báo cáo không
            phải sửa route). Khác "/reports" cũ — trang cũ giữ nguyên không đụng. */}
            <Route
              path="report-center"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <ReportCenterPage />
                    </S>
                  }
                  requiredPermissions={[...REPORT_CENTER_ANY_PERMISSIONS]}
                />
              }
            />
            <Route
              path="report-center/:reportId"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <ReportOutletPage />
                    </S>
                  }
                  requiredPermissions={[...REPORT_CENTER_ANY_PERMISSIONS]}
                />
              }
            />

            {/* Reports — SITEMAP §151-161 */}
            <Route path="reports">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="reports-basic" minTier="lite">
                          <ReportsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "reports.revenue",
                      "reports.inventory",
                    ]}
                  />
                }
              />
              <Route
                path="revenue"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="reports-basic" minTier="lite">
                          <SalesReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["reports.revenue"]}
                  />
                }
              />
              <Route
                path="inventory"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="reports-basic" minTier="lite">
                          <InventoryReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["reports.inventory"]}
                  />
                }
              />
              {/*
                            RBAC fix (2026-09-02, whole-branch review — 2 lượt):
                            Lượt 1 — trước fix, route này CHỈ khoá bằng RoleGuard(['owner','manager'])
                            (role-string check, không phải permission-key), không có requiredPermissions
                            riêng ở PrivateRoute. Task F2.2 (menuItems.tsx, nhóm "Tài chính") vừa mở menu
                            item "Cuối ngày (EOD)" cho accountant/ke_toan_truong qua key
                            'accounting.reports' — nhưng RoleGuard ở đây không biết 2 role đó, nên họ
                            thấy menu item, bấm vào, ăn 403 ngay (dead-end button). Không thể chỉ "widen
                            allowedRoles thêm ke_toan_truong" — 'ke_toan_truong' KHÔNG nằm trong union
                            type `UserRole`/`StaffRole` (useAuth.ts/authSlice.ts, model role cũ/hẹp hơn
                            hệ role BE thật) nên sẽ không type-check. Chuyển hẳn sang permission-key
                            check ở PrivateRoute, bỏ hẳn RoleGuard khỏi route này — nhưng lượt 1 dùng SAI
                            key ('accounting.reports', suy luận từ key đang mở group menu ngoài, KHÔNG
                            verify lại đúng route/controller thật đứng sau màn hình).
                            Lượt 2 (sửa lại) — route/controller thật FE gọi là
                            `eod-report/controllers/eod.controller.ts` (`@Controller('eod')`, xem
                            `eod.service.ts`; `reports/eod/controllers/eod.controller.ts` — namespace
                            `@Controller('reports/eod')` — là controller SONG SONG, xác nhận KHÔNG có FE
                            call site nào tới, không phải route đang dùng) — controller đó đòi ĐÚNG
                            'reports.eod.view' cho các route đọc (history/getByDate/pdf) và
                            'reports.eod.manage' cho route ghi (generate/close/patchReconciliation);
                            route "reopen" giữ nguyên owner-only hardcode, không qua permission key nào.
                            'accounting.reports' KHÔNG được controller này tra ở bất kỳ đâu — gd_kd (Giám
                            đốc KD) giữ 'reports.eod.view' nhưng KHÔNG giữ 'accounting.reports'
                            (role-permissions.service.ts khối `gd_kd`) nên vẫn bị 403 y hệt lượt 1, dù
                            group menu "Tài chính" họ đã mở được từ trước (qua 'reports.revenue' của
                            COGS, không phải qua EOD). Đổi đúng sang 'reports.eod.view' — route FE này
                            chỉ ĐỌC báo cáo (trang không tự tách UI theo .view/.manage), khớp đúng nhóm
                            hành động GET của controller thật.
                            Lưu ý (thông tin, không cần hành động): module `eod-report`
                            (`EodReportModule`) hiện đang bị comment `DISABLED_FOR_V1` khỏi
                            `app.module.ts` (BE, sibling repo) — route API thật KHÔNG sống trên môi
                            trường hiện tại nên không thể tự verify 200/403 sống ngay lúc này; đây vẫn
                            là key ĐÚNG cần khớp cho khi module được bật lại.
                        */}
              <Route
                path="eod"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="reports-basic" minTier="lite">
                          <EodReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["reports.eod.view"]}
                  />
                }
              />
              <Route
                path="pawn"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <PawnReportsPage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="installment"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <InstallmentReportsPage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="workshop"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <WorkshopReportsPage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="exchange"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="exchange" minTier="starter">
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <ExchangeReportsPage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="buyback"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="buyback" minTier="starter">
                          <RoleGuard allowedRoles={["owner", "manager"]}>
                            <BuybackReportsPage />
                          </RoleGuard>
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="accounting"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <AccountingReportsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="business-results"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <BusinessResultsReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="balance-sheet"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <BalanceSheetReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="trial-balance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <TrialBalanceReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="s2b-revenue"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <S2bRevenueLedgerPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="s2c-income-expense"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <S2cIncomeExpenseDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="s2d-materials"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <S2dMaterialsDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="s2e-cash"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <S2eCashDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              {/* RBAC fix (2026-09-02, Task F2.3) — "Tổng hợp NXT" trang này (pages/AccountingReports/
                            inventory-summary.tsx) thực tế gọi inventoryService.getSummary() → GET /inventory/reports/summary,
                            gate BE bằng 'inventory.reports.view' (KHÔNG phải 'accounting.reports' — trang không hề gọi
                            accountingService.getInventorySummaryReport(), đó là 1 hàm service khác/không dùng ở trang này).
                            Route guard cũ chỉ đòi 'accounting.reports' khiến Thủ kho (inventory)/NV Kho (nv_kho) — cả 2 đã
                            có 'inventory.reports.view' qua wildcard 'inventory.*'/gán tường minh — bấm vào từ menu "Kho hàng"
                            (đã hiện sẵn, không có gate riêng ở menuItems.tsx) vẫn bị 403. Thêm OR 'inventory.reports.view',
                            GIỮ NGUYÊN 'accounting.reports' cho manager/accountant/ke_toan_truong. */}
              <Route
                path="inventory-summary"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <InventorySummaryReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "accounting.reports",
                      "inventory.reports.view",
                    ]}
                  />
                }
              />
              <Route
                path="cash-book"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <CashBookPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="bank-deposit-book"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <BankDepositBookPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="journal-entries/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <JournalEntryDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="journal-entries"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <JournalEntriesReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="document-listing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <DocumentListingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="voucher-listing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <VoucherListingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="invoice-listing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <InvoiceListingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="warehouse-voucher-listing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <WarehouseVoucherListingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="receivable-payable-listing"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <ReceivablePayableListingPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="customers-ar"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          {/* Canonical /reports/customers-ar page; print layout lives in ar-summary.tsx. */}
                          <ArSummaryPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="ar-detail"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <ArDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="sales-summary-by-product"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <SalesSummaryByProductPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
              <Route
                path="sales-detail"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting-reports"
                          minTier="professional"
                        >
                          <SalesDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.reports"]}
                  />
                }
              />
            </Route>

            {/* Settings — SITEMAP §185-197 */}
            <Route path="settings">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <SettingsIndexPage />
                      </S>
                    }
                  />
                }
              />
              {/* EG-345 P1 — Business info form (was StoreSettingsPage placeholder) */}
              <Route
                path="business"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <BusinessSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.business"]}
                  />
                }
              />
              {/* StoreSettings: /settings/stores → quản lý chi nhánh (preserve for sitemap) */}
              <Route
                path="stores"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <StoreSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.business"]}
                  />
                }
              />
              <Route
                path="appearance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ThemeSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.theme"]}
                  />
                }
              />
              <Route
                path="hardware"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <HardwarePage />
                      </S>
                    }
                    requiredPermissions={["settings.hardware"]}
                  />
                }
              />
              {/* Accounting Settings — S4.1 FE Accounting */}
              {/* RBAC fix (2026-09-02, Task F2.4) — hub "Cài đặt kế toán" (danh mục tài khoản/tiền tệ/
                            danh mục thu-chi/quỹ...) — các route mutate thật bên trong (account.controller.ts,
                            currency.controller.ts, expense-income-category.controller.ts, fund-account.controller.ts,
                            account-tree.controller.ts) đều đòi 'accounting.settings.general' (KHÔNG phải
                            'accounting.settings' rộng). Excel row 131: chỉ Owner + manager (bảo toàn hành vi
                            @Roles('owner','manager') cũ, xem role-permissions.service.ts) — accountant/ke_toan_truong
                            để TRỐNG dù cả 2 đang giữ 'accounting.settings' rộng nên trước đây vẫn mở được UI (dù mọi
                            action mutate sẽ 403 vì BE đã đòi đúng '.general'). Đổi đúng key hẹp — giữ nguyên quyền
                            manager, loại accountant/ke_toan_truong khỏi UI khớp đúng Excel. */}
              <Route
                path="accounting"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <AccountingSettingsPage />
                      </S>
                    }
                    requiredPermissions={["accounting.settings.general"]}
                  />
                }
              />
              {/* Roles — NEW, EXTENSION BEYOND SITEMAP v1.1 */}
              <Route
                path="roles"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RolesPage />
                      </S>
                    }
                    requiredPermissions={["settings.roles"]}
                  />
                }
              />
              <Route
                path="roles/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleDetailPage />
                      </S>
                    }
                    requiredPermissions={["settings.roles"]}
                  />
                }
              />
              {/* Personal settings — all authenticated users */}
              <Route
                path="profile"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ProfileSettingsPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="security"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <SecuritySettingsPage />
                      </S>
                    }
                  />
                }
              />
              {/* Tenant-level settings — owner/manager */}
              <Route
                path="notifications"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <NotificationsSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.notifications"]}
                  />
                }
              />
              <Route
                path="general"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <GeneralSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.manage"]}
                  />
                }
              />
              <Route
                path="locale"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <LocaleSettingsPage />
                      </S>
                    }
                    requiredPermissions={["settings.locale"]}
                  />
                }
              />
              <Route
                path="data-export"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <DataExportPage />
                      </S>
                    }
                    requiredPermissions={["data_export.manage"]}
                  />
                }
              />
              {/* EG-508 — Payroll settings */}
              <Route
                path="payroll"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <PayrollSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["settings.payroll"]}
                  />
                }
              />
              {/*
                            Màn hình cài đặt mẫu phải cùng gate với menu. Các API GET template
                            vẫn mở cho luồng POS đọc template mặc định; gate này chỉ bảo vệ UI
                            cài đặt khỏi role vận hành như thủ kho.
                        */}
              <Route
                path="receipt-templates"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ReceiptTemplateListPage />
                      </S>
                    }
                    requiredPermissions={["settings.manage"]}
                  />
                }
              />
              {/* TCR-1740 — route soạn thảo vẫn dùng quyền ghi riêng; menu/list ở trên
                            dùng quyền cài đặt chung để thủ kho không truy cập UI cài đặt. */}
              <Route
                path="receipt-templates/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ReceiptTemplateEditorPage />
                      </S>
                    }
                    requiredPermissions={["settings.receipt_templates.manage"]}
                  />
                }
              />
              <Route
                path="receipt-templates/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ReceiptTemplateEditorPage />
                      </S>
                    }
                    requiredPermissions={["settings.receipt_templates.manage"]}
                  />
                }
              />
            </Route>

            {/* Payroll Components — EG-509 */}
            <Route path="payroll">
              {/* Payroll Dashboard index — EG-525 */}
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <PayrollSummaryDashboard />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.view"]}
                  />
                }
              />
              <Route
                path="components"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <PayrollComponentsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.manage"]}
                  />
                }
              />
              {/* Payroll Runs — EG-517 */}
              <Route
                path="runs"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <PayrollRunsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.view"]}
                  />
                }
              />
              {/* Payroll Run Detail — EG-518 */}
              <Route
                path="runs/:runId"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <PayrollRunDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.view"]}
                  />
                }
              />
              {/* S5-HKD Payroll Register Report — EG-526 */}
              <Route
                path="reports/s5-hkd"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <S5HKDPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.view"]}
                  />
                }
              />
              {/* TNCN Quyết toán Mẫu 02 Report — EG-527 */}
              <Route
                path="reports/qtt-tncn"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="payroll" minTier="starter">
                          <QTTTNcnPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["payroll.view"]}
                  />
                }
              />
            </Route>

            {/* Self-service Payslip History — EG-520 (all authenticated users) */}
            <Route
              path="me/payslips"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="payroll" minTier="starter">
                        <PayslipHistoryPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />

            {/* Suppliers + Purchasing — SITEMAP §83-92 [S]+ */}
            <Route path="suppliers">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="suppliers" minTier="starter">
                          <SuppliersPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="suppliers" minTier="starter">
                          <SupplierFormPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/history"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="suppliers" minTier="starter">
                          <SupplierHistoryPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="suppliers" minTier="starter">
                          <SupplierDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="suppliers" minTier="starter">
                          <SupplierFormPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            <Route
              path="accounting/sales/gift-orders"
              element={
                <PrivateRoute
                  requiredPermissions={["accounting.vouchers"]}
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="starter">
                        <GiftOrdersPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            {/* AMIS-style Sales Documents — chứng từ bán hàng */}
            <Route path="sales/documents">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoicesPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoiceCreatePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="returns/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesReturnDrawerPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="discounts/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesDiscountNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="returns/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <DerivedSalesDocumentDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="returns/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <DerivedSalesDocumentEditPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="discounts/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <DerivedSalesDocumentDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="discounts/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesDiscountNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            {/* Sales Invoices legacy routes — EG-568, EG-569 */}
            <Route path="sales/invoices">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoicesPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoiceCreatePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="replacement"
                element={
                  <Navigate to="/sales/e-invoices?type=replacement" replace />
                }
              />
              <Route
                path="replacement/new"
                element={
                  <Navigate to="/sales/e-invoices?type=replacement" replace />
                }
              />
              <Route
                path="returns"
                element={
                  <Navigate to="/sales/e-invoices?type=return" replace />
                }
              />
              <Route
                path="returns/new"
                element={
                  <Navigate to="/sales/e-invoices?type=return" replace />
                }
              />
              <Route
                path="adjustment"
                element={
                  <Navigate to="/sales/e-invoices?type=adjustment" replace />
                }
              />
              <Route
                path="adjustment/new"
                element={
                  <Navigate to="/sales/e-invoices?type=adjustment" replace />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <SalesInvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <LegacySalesInvoiceEditEntryPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            {/* AMIS-style quản lý hóa đơn chung — gốc + điều chỉnh + thay thế + trả hàng */}
            <Route path="sales/e-invoices">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="sales-invoices"
                          minTier="professional"
                        >
                          <InvoiceManagementPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            <Route path="accounting/purchasing">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingReportsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports/ap-summary"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <ApSummaryPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports/ap-detail"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <ApDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports/purchase-ledger"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchaseLedgerPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="receiving"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingReceivingListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "purchasing.orders.view",
                      "purchasing.orders.manage",
                    ]}
                  />
                }
              />
              <Route
                path=":id/receive"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingReceivePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/match"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingMatchPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="purchasing" minTier="starter">
                          <PurchasingDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            {/*
             * E-Invoices — AMIS refactor.
             *
             * Gate đọc HĐĐT dùng `accounting.einvoice.view`. BE đổi tên key quyền ĐỌC
             * từ `accounting.einvoice` (bare) sang `accounting.einvoice.view` cho các route
             * GET /e-invoices*; `.manage` (POST/PATCH) và `.delete` giữ nguyên. Đây là đổi
             * TÊN thuần, không đổi ngữ nghĩa: key cũ vốn đã là quyền xem, nên route `new` /
             * `:id/edit` / `settings` vẫn giữ nguyên mức gate như trước (BE mới là nơi
             * chặn ghi, FE không tự siết thêm để tránh lệch quyền với BE).
             */}
            <Route path="accounting/e-invoices">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EInvoiceListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EInvoiceFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EInvoiceFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <AccountingEInvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path="report"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EInvoiceReportPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
            </Route>
            <Route path="accounting/purchase-invoices">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseInvoicesPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseInvoiceNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new-import"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseInvoiceImportNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseInvoiceNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseInvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            {/* Purchase Returns — EG-576 */}
            <Route path="accounting/purchase-returns">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseReturnsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseReturnNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseReturnDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>
            {/* Purchase Discounts — EG-577 */}
            <Route path="accounting/purchase-discounts">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseDiscountsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseDiscountNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseDiscountNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="accounting" minTier="starter">
                          <PurchaseDiscountDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Promotions — SITEMAP §170-173 [S]+, Flash Sale [P] */}
            <Route path="promotions">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <PromotionsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.promotions.view"]}
                  />
                }
              />
              <Route
                path="vouchers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <VouchersPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.vouchers.view"]}
                  />
                }
              />
              <Route
                path="flash-sales"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="flash-sales"
                          minTier="professional"
                        >
                          <FlashSalesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.flash_sales.view"]}
                  />
                }
              />
              <Route
                path="flash-sales/create"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="flash-sales"
                          minTier="professional"
                        >
                          <FlashSaleCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.flash_sales.manage"]}
                  />
                }
              />
              <Route
                path="flash-sales/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="flash-sales"
                          minTier="professional"
                        >
                          <FlashSaleCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "marketing.flash_sales.view",
                      "marketing.flash_sales.manage",
                    ]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <PromotionCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.promotions.manage"]}
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <PromotionCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "marketing.promotions.view",
                      "marketing.promotions.manage",
                    ]}
                  />
                }
              />
              <Route
                path="vouchers/create"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <VoucherCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.vouchers.manage"]}
                  />
                }
              />
              <Route
                path="vouchers/:id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <VoucherCreatePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "marketing.vouchers.view",
                      "marketing.vouchers.manage",
                    ]}
                  />
                }
              />
              <Route
                path="loyalty"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <LoyaltyPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["loyalty.view"]}
                  />
                }
              />
              <Route
                path="wholesale-gifts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="promotions"
                          minTier="professional"
                        >
                          <WholesaleGiftsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.wholesale_gifts.view"]}
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="promotions" minTier="starter">
                          <PromotionReportsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["marketing.reports.view"]}
                  />
                }
              />
            </Route>

            {/* Workshop — SITEMAP §65-74 [S]+ */}
            <Route path="workshop">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <WorkshopPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="craftsmen"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <CraftsmenPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="services"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <WorkshopServicesPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="commissions"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <WorkshopCommissionsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <WorkshopNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={<Navigate to={ROUTES.reports.workshop} replace />}
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="workshop" minTier="starter">
                          <WorkshopDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Commission — EG-350 [S]+ — gated tier Starter trở lên */}
            <Route path="commissions">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="commission" minTier="starter">
                          <CommissionDashboardPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="my"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CommissionMyPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="kpi"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CommissionKpiPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="leaderboard"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CommissionLeaderboardPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="config"
                element={<Navigate to="/commissions/config/general" replace />}
              />
              <Route
                path="payouts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionPayoutsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="kpi-targets"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner", "manager"]}>
                          <CommissionKpiTargetsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="config/general"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionGeneralConfigPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="config/roles"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionRolesPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="config/product-rates"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionProductRatesPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="config/bonus-thresholds"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionBonusThresholdsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="adjustments"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionAdjustmentsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="live-sessions"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner", "manager"]}>
                          <CommissionLiveSessionsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner", "manager"]}>
                          <CommissionReportsPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="review"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionReviewIndexPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="review/:period/:staffId"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <RoleGuard allowedRoles={["owner"]}>
                          <CommissionReviewDetailPage />
                        </RoleGuard>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Pawn — SITEMAP §33-42 [S]+ */}
            <Route path="pawn">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnListPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="due-today"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnDueTodayPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="overdue"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnOverduePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={<Navigate to={ROUTES.reports.pawn} replace />}
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="pawn" minTier="starter">
                          <PawnDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Installment — SITEMAP §43-51 [P] (tích hợp BIDV) */}
            <Route path="installment">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentListPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="collections"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentCollectionsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={<Navigate to={ROUTES.reports.installment} replace />}
              />
              <Route
                path=":id/collect"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentCollectPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/schedule"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentSchedulePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="installment"
                          minTier="professional"
                        >
                          <InstallmentDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Consignment — SITEMAP §52-58 [S]+ */}
            <Route path="consignment">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="consignment" minTier="starter">
                          <ConsignmentListPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="consignment" minTier="starter">
                          <ConsignmentNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="consignment" minTier="starter">
                          <ConsignmentReportsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="consignment" minTier="starter">
                          <ConsignmentDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Labels — SITEMAP §118-124 [S]+ */}
            <Route path="labels">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelEditorPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelEditorPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id/preview"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelPreviewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="printer-settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelsPrinterSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="print"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="labels" minTier="starter">
                          <LabelPrintBatchPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Finance — SITEMAP §138-150 [S]+ HĐĐT, [E] AMIS */}
            <Route path="finance">
              <Route
                path="eod"
                element={<PrivateRoute element={<FinanceEodRedirect />} />}
              />
              <Route
                path="eod/:date"
                element={<PrivateRoute element={<FinanceEodRedirect />} />}
              />
              <Route
                path="cash-book"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FinanceCashBookPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="vouchers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FinanceVouchersPage />
                      </S>
                    }
                  />
                }
              />
              <Route
                path="invoices"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <FinanceInvoicesPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="invoices/issue"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <FinanceInvoiceIssuePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="invoices/:id/adjust"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <FinanceInvoiceAdjustPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="invoices/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <FinanceInvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="einvoice-settings"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceSettingsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="einvoices"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceListPage />
                        </FeatureGate>
                      </S>
                    }
                    // Route menu "Tài chính > Hoá đơn điện tử" trước đây KHÔNG có gate quyền: user không có
                    // quyền HĐĐT (vd nv_kho) gõ thẳng URL vẫn vào được khung trang (kèm nút "Tạo thủ công"),
                    // dù BE trả 403 nên không lộ dữ liệu. Gate cùng key xem với menu/route accounting/e-invoices.
                    requiredPermissions={["accounting.einvoice.view"]}
                  />
                }
              />
              <Route
                path="einvoices/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="einvoices/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceDetailPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="einvoice/reports/stats"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceStatsPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="einvoice/reports/tax"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="e-invoice" minTier="starter">
                          <EinvoiceTaxReportPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="payment-methods"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <PaymentMethodsPage />
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Multi-store — SITEMAP §131-137 [P] */}
            <Route path="stores">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="multi-store"
                          minTier="professional"
                        >
                          <StoresListPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="multi-store"
                          minTier="professional"
                        >
                          <StoreNewPage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute element={<Navigate to="edit" replace />} />
                }
              />
              <Route
                path=":id/edit"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="multi-store"
                          minTier="professional"
                        >
                          <StoresEditPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["stores.manage"]}
                  />
                }
              />
              <Route
                path=":id/hardware/*"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="multi-store"
                          minTier="professional"
                        >
                          <StoreHardwarePage />
                        </FeatureGate>
                      </S>
                    }
                  />
                }
              />
            </Route>

            {/* Reports advanced — SITEMAP §152-161 [P]/[E] */}
            <Route
              path="reports/profit"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="reports-advanced"
                        minTier="professional"
                      >
                        <ProfitReportPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="reports/staff"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="reports-advanced"
                        minTier="professional"
                      >
                        <StaffReportPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="reports/customers"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="reports-advanced"
                        minTier="professional"
                      >
                        <CustomersReportPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="reports/products"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="reports-basic" minTier="lite">
                        <ProductsReportPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="reports/multi-store"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="reports-advanced"
                        minTier="professional"
                      >
                        <MultiStoreReportPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="reports/builder"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate
                        feature="reports-advanced"
                        minTier="enterprise"
                      >
                        <ReportBuilderPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />

            {/* Import Wizard — SITEMAP §179-184 [S]+ */}
            <Route path="import">
              <Route
                path="jobs/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportJobDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "products.create",
                      "customers.manage",
                      "customers.create",
                      "suppliers.view",
                      ...HR_IMPORT_ANY_WRITE_PERMISSION,
                    ]}
                  />
                }
              />
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportHubPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "products.create",
                      "customers.manage",
                      "customers.create",
                      "suppliers.view",
                      ...HR_IMPORT_ANY_WRITE_PERMISSION,
                    ]}
                  />
                }
              />
              <Route
                path="products"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportProductsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["products.create"]}
                  />
                }
              />
              {/* TCR-1919 — `POST /v1/customers/import` nay nhận thêm 'customers.create'
                            (import hàng loạt = tạo khách hàng) sau khi key gộp 'customers.manage'
                            nghỉ hưu và bị gỡ khỏi mọi vai trò. */}
              <Route
                path="customers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportCustomersPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      "customers.manage",
                      "customers.create",
                    ]}
                  />
                }
              />
              <Route
                path="suppliers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportSuppliersPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["suppliers.view"]}
                  />
                }
              />
              <Route
                path="employees"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportEmployeesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.employees,
                    ]}
                  />
                }
              />
              <Route
                path="benefits"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportBenefitsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.benefits,
                    ]}
                  />
                }
              />
              <Route
                path="payrolls"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportPayrollsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.payrolls,
                    ]}
                  />
                }
              />
              <Route
                path="contracts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportContractsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.contracts,
                    ]}
                  />
                }
              />
              <Route
                path="insurances"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportInsurancesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.insurances,
                    ]}
                  />
                }
              />
              <Route
                path="insurance-roster"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportInsuranceRosterPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.insuranceRoster,
                    ]}
                  />
                }
              />
              <Route
                path="attendance"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate feature="csv-import" minTier="starter">
                          <ImportAttendancePage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={[
                      ...HR_IMPORT_WRITE_PERMISSIONS.attendance,
                    ]}
                  />
                }
              />
            </Route>

            {/* EG-348 Phase D — CRM 360 */}
            {/* RBAC fix (TCR-1600) — route trước đây KHÔNG có requiredPermissions, chỉ gate
                    theo plan tier (FeatureGate) — role thiếu crm.view vẫn vào được trang dù menu đã
                    ẩn (bookmark/gõ URL trực tiếp). API thật (crm.controller.ts) đòi đúng crm.view. */}
            <Route
              path="crm"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="crm" minTier="professional">
                        <CrmDashboardPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["crm.view"]}
                />
              }
            />
            <Route
              path="crm/follow-ups"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="crm" minTier="professional">
                        <FollowUpsBoardPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["crm.view"]}
                />
              }
            />

            {/* EG-375 / EG-335 — Referral Program (Starter+ tier) */}
            <Route
              path="referral"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="referral" minTier="starter">
                        <ReferralOverviewPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="referral/list"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="referral" minTier="starter">
                        <ReferralListPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="referral/leaderboard"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="referral" minTier="starter">
                        <ReferralLeaderboardPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="referral/config"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="referral" minTier="starter">
                        <ReferralConfigPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />

            {/* Audit log + API Keys — under settings namespace */}
            <Route
              path="settings/audit-log"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="audit-log" minTier="professional">
                        <AuditLogPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="settings/migrations"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <MigrationLogPage />
                    </S>
                  }
                />
              }
            />
            <Route
              path="settings/worker-monitor"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <RoleGuard allowedRoles={["owner", "manager"]}>
                        <WorkerMonitorPage />
                      </RoleGuard>
                    </S>
                  }
                />
              }
            />
            {/* TCR-2653 — Giám sát hệ thống: cùng phạm vi role với API (OWNER, MANAGER). */}
            <Route
              path="settings/ops-dashboard"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <RoleGuard allowedRoles={["owner", "manager"]}>
                        <OpsDashboardPage />
                      </RoleGuard>
                    </S>
                  }
                />
              }
            />
            <Route
              path="settings/action-logs"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="audit-log" minTier="professional">
                        <SystemLogsPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />
            <Route
              path="settings/api-keys"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="api-keys" minTier="enterprise">
                        <ApiKeysPage />
                      </FeatureGate>
                    </S>
                  }
                />
              }
            />

            {/* Support — SITEMAP §198-201 (all users). TCR-1743: "Trung tâm hỗ trợ" đã
                        gỡ hẳn, chỉ còn Changelog. Khai route PHẲNG `support/changelog` thay vì
                        giữ route cha `support` rỗng: route cha vẫn KHỚP `/support` và render một
                        Outlet trống ⇒ trang trắng, không rơi được vào catch-all NotFound. */}
            <Route
              path="support/changelog"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <ChangelogPage />
                    </S>
                  }
                />
              }
            />

            {/* Shipping — EG-351 */}
            {/*
                        RBAC fix (2026-09-02, whole-branch review fix round) — key cũ 'shipping.view' đã bị
                        BE (Task B2.3, sibling repo tcr-erp-be-tenant) XOÁ HẲN khỏi catalogue, tách thành 4
                        key riêng theo đúng 4 màn hình con của module Giao vận:
                          - 'shipping.orders.view'            → Đơn giao vận (list + detail)
                          - 'shipping.cod_reconciliation.view' → Đối soát COD (list + detail)
                          - 'shipping.pickup_locations.view'/'.manage' → Nơi lấy hàng
                          - 'shipping.logs.view'               → Logs vận chuyển (kể cả route cũ ghtk-logs)
                        Map lại từng route dưới đây đúng key con của MÀN HÌNH đó (không dùng chung 1 key
                        gộp nữa) — xem migrate note đầy đủ ở đầu shipping.controller.ts (BE) cho danh sách
                        route → key gốc.
                    */}
            <Route path="shipping">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ShippingListPage />
                      </S>
                    }
                    requiredPermissions={["shipping.orders.view"]}
                  />
                }
              />
              <Route
                path="new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ShippingNewPage />
                      </S>
                    }
                    requiredPermissions={["shipping.manage"]}
                  />
                }
              />
              {/*
                            RBAC fix (2026-09-02, whole-branch review — 2 lượt):
                            Lượt 1 từng narrow route này thẳng xuống 'shipping.pickup_locations.manage'
                            (đúng lý do: route ghi add/update pickup-location BE đòi .manage, page trước
                            đó hiện nút Thêm/Sửa vô điều kiện) nhưng SAI HƯỚNG — verify lại
                            role-permissions.service.ts khối `shipper` xác nhận shipper CHỈ được cấp
                            'shipping.pickup_locations.VIEW' (comment gốc: "Excel cột Người giao hàng
                            (Shipper) = Đọc trên CẢ 4 dòng ... giữ NGUYÊN đủ cả 4, không narrowing"), và
                            route GET pickup-locations (list) trên BE chỉ đòi đúng '.view' — CHỈ route
                            POST/PATCH (add/update) mới đòi '.manage'. Gate cả ROUTE ở mức '.manage' như
                            lượt 1 khiến shipper mất hẳn quyền xem màn này — regression so với `dev`
                            (nơi gate cũ là 'shipping.manage' rộng, shipper vẫn có).
                            Lượt 2: trả route/menu về đúng '.view' (khớp route GET thật + giữ nguyên
                            quyền đọc của shipper), và chuyển việc tách đọc/ghi xuống ĐÚNG tầng UI bên
                            trong trang (PickupLocationsPage: nút "Thêm"/"Sửa" + cột "Thao tác" chỉ hiện
                            khi có 'shipping.pickup_locations.manage', xem pickup-locations.tsx) — không
                            còn chặn cả trang.
                        */}
              <Route
                path="pickup-locations"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <PickupLocationsPage />
                      </S>
                    }
                    requiredPermissions={["shipping.pickup_locations.view"]}
                  />
                }
              />
              <Route
                path="cod-reconciliation"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CodReconciliationPage />
                      </S>
                    }
                    requiredPermissions={["shipping.cod_reconciliation.view"]}
                  />
                }
              />
              <Route
                path="cod-reconciliation/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CodReconciliationDetailPage />
                      </S>
                    }
                    requiredPermissions={["shipping.cod_reconciliation.view"]}
                  />
                }
              />
              <Route
                path="cod-remittances"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <CodRemittancesPage />
                      </S>
                    }
                    requiredPermissions={["shipping.cod.manage"]}
                  />
                }
              />
              <Route
                path="logs"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ShippingLogsPage />
                      </S>
                    }
                    requiredPermissions={["shipping.logs.view"]}
                  />
                }
              />
              {/* Route cũ vẫn giữ để link cũ không gãy; đã ẩn khỏi menu. */}
              <Route
                path="ghtk-logs"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <GhtkLogsPage />
                      </S>
                    }
                    requiredPermissions={["shipping.logs.view"]}
                  />
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <ShippingDetailPage />
                      </S>
                    }
                    requiredPermissions={["shipping.orders.view"]}
                  />
                }
              />
            </Route>

                    {/* Accounting Dashboard — EG-605 */}
                    <Route
                        path="accounting/dashboard"
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><DashboardOverviewPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                    />
                    <Route
                        path="accounting/data-repair"
                        // RBAC fix (2026-09-02, Task F2.4) — accounting-data-repair.controller.ts (3 route: audit/
                        // repair-balances/repair-safe) đòi đúng 'accounting.settings.repair' (Excel row 130: "chỉ dành
                        // cho Owner" — KHÔNG role hệ thống nào khác được gán key này, owner chạm tới DUY NHẤT qua
                        // wildcard '*'). Route guard cũ dùng key rộng 'accounting.settings' (manager/accountant/
                        // ke_toan_truong đều giữ) khiến 3 role đó mở được UI dù API mutate 403 ngay — đổi đúng key hẹp
                        // route/API thật yêu cầu.
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><AccountingDataRepairPage /></FeatureGate></S>} requiredPermissions={['accounting.settings.repair']} />}
                    />
                    <Route path="accounting/document-scenarios/test" element={<PrivateRoute element={<S><DocumentScenarioTestPage /></S>} requiredPermissions={['accounting.scenarios.manage']} />} />
                    <Route
                        path="accounting/document-scenarios"
                        // Admin CRUD for the ecom document-scenario engine's configuration
                        // (tcr-erp-be-tenant Phase 3 backend). Internal config surface, not a
                        // billed accounting feature — deliberately no <FeatureGate> wrap, unlike
                        // sibling accounting/* routes.
                        element={<PrivateRoute element={<S><DocumentScenariosPage /></S>} requiredPermissions={['accounting.scenarios.manage']} />}
                    />

            {/* AMIS Sync Master Data */}
            <Route
              path="accounting/amis/outbox/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <AmisOutboxDetailPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.settings"]}
                />
              }
            />
            <Route
              path="accounting/amis/:tab/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <AmisMasterdataDetailPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.settings"]}
                />
              }
            />
            <Route
              path="accounting/amis/:tab"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <AmisSyncPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.settings"]}
                />
              }
            />
            <Route
              path="accounting/amis"
              element={<Navigate to={ROUTES.accounting.amisConnect} replace />}
            />

            {/* Accounting Transactions — S4.1 FE Accounting (EG-583 to EG-588) */}
            <Route path="accounting/transactions">
              <Route
                index
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <TransactionsListPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="receipt/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ReceiptFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="receipt/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ReceiptFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="payment/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <PaymentFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="payment/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <PaymentFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="internal-transfer/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <InternalTransferFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="internal-transfer/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <InternalTransferFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="prepaid-expenses"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <PrepaidExpensesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="prepaid-expenses/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <PrepaidExpenseFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="prepaid-expenses/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <PrepaidExpenseFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="other/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <OtherDocumentPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="other/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <OtherDocumentPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="other"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <OtherDocumentPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="fixed-asset-groups"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <FixedAssetGroupsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="fixed-assets"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <FixedAssetsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="fixed-assets/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <FixedAssetFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="fixed-assets/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <FixedAssetDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="tool-supplies"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ToolSuppliesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="tool-supplies/reports"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ToolSupplyReportsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="tool-supplies/new"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ToolSupplyFormPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
              <Route
                path="tool-supplies/:id"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ToolSupplyDetailPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.vouchers"]}
                  />
                }
              />
            </Route>
            <Route
              path="accounting/cash"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <TransactionsListPage mode="cash" />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.vouchers"]}
                />
              }
            />
            <Route
              path="accounting/bank-deposits"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <BankDepositsPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.vouchers"]}
                />
              }
            />

            {/* Accounting Purchase Invoices — EG-575 to EG-577 (moved to /accounting/purchasing group above) */}

            {/* Gov E-Invoices — EG-578 */}
            <Route
              path="accounting/gov-invoices/:id"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <GovInvoiceDetailPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.einvoice.view"]}
                />
              }
            />
            <Route
              path="accounting/gov-invoices"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <GovInvoicesPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.einvoice.view"]}
                />
              }
            />

            {/* Accounting Inventory — EG-579 to EG-582 */}
            <Route path="accounting/inventory">
              <Route
                path="receipts"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <AccountingReceiptsPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.inventory"]}
                  />
                }
              />
              <Route
                path="issues"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <AccountingIssuesPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.inventory"]}
                  />
                }
              />
              <Route
                path="transfers"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <AccountingTransfersPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.inventory"]}
                  />
                }
              />
              <Route
                path="products-uom"
                element={
                  <PrivateRoute
                    element={
                      <S>
                        <FeatureGate
                          feature="accounting"
                          minTier="professional"
                        >
                          <ProductsUomPage />
                        </FeatureGate>
                      </S>
                    }
                    requiredPermissions={["accounting.inventory"]}
                  />
                }
              />
            </Route>

            {/* Customers AR — EG-573 */}
            <Route
              path="accounting/customers/ar"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <CustomerARManagementPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.ar"]}
                />
              }
            />
            <Route
              path="accounting/suppliers/ap"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <SupplierAPManagementPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.ar"]}
                />
              }
            />
            <Route
              path="accounting/debt-settlements/customer-document-offset"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <CustomerDocumentOffsetPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.debt_settlements.view"]}
                />
              }
            />
            <Route
              path="accounting/debt-settlements/customer-debt-offset"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <CustomerDebtOffsetPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.debt_settlements.view"]}
                />
              }
            />
            <Route
              path="accounting/debt-settlements/remove-customer-document-offset"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <RemoveCustomerDocumentOffsetPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.debt_settlements.view"]}
                />
              }
            />
            <Route
              path="accounting/debt-settlements/customer-document-offset-multi"
              element={
                <PrivateRoute
                  element={
                    <S>
                      <FeatureGate feature="accounting" minTier="professional">
                        <CustomerDocumentOffsetMultiPage />
                      </FeatureGate>
                    </S>
                  }
                  requiredPermissions={["accounting.debt_settlements.view"]}
                />
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </RouteErrorBoundary>
    </>
  );
}

export default App;
